/**
 * AI 소개영상 모달 — 상세설정의 일정 데이터와 상세정보 콘텐츠 글로 짧은 소개 영상을 만들어 콘텐츠로 붙인다.
 *
 * 흐름 (4단계):
 *   1) 대본     — 일정·상세정보 콘텐츠 글·상품 사진(썸네일)을 erp-video 에 보내 대본 + 장면별 사진 선택을 받는다. 사람이 고칠 수 있다.
 *   2) 미리보기 — 장면별 나레이션(TTS)을 만들고, 캔버스에서 소리와 함께 실시간 재생해 본다. 인코딩 전이라 수정이 빠르다.
 *   3) 내보내기 — 고른 형식(16:9 / 9:16 / 1:1)마다 mp4 를 인코딩한다.
 *   4) 첨부     — 새 콘텐츠를 만들고 mp4 들을 첨부파일로 올린 뒤, 본문에 <video> 를 넣어 저장한다.
 *
 * 장면 사진은 AI 가 그리는 게 아니라 이 상품에 등록된 실제 사진(대표이미지·일정 사진)만 쓴다.
 * AI 는 "어떤 사진이 어느 장면에 어울리는지" 를 고를 뿐이다. 그래서 실제와 다른 장면이 실릴 일이 없고,
 * 비용은 대본 1회(사진 포함 ≈ $0.02) + 나레이션 몇 초(≈ $0.02) 수준이다.
 *
 * 고급 옵션 "AI 영상(Veo)": 장면 사진을 첫 프레임으로 넣어 Veo 가 "그 사진이 움직이는" 클립을 만들고, 조립기는 사진 대신
 * 그 클립 위에 자막·나레이션·음악·전환을 얹는다. 기본은 꺼짐 — 초당 과금(720p: lite $0.05 · fast $0.10 · standard $0.40)이라
 * 만들기 전에 견적을 보여 주고 확인을 받는다. 같은 재료로 만든 클립은 브라우저(IndexedDB)에 두고 다시 쓴다(clipCache.js).
 * 실패한 장면(안전필터 등)은 사진 켄번즈로 돌아간다. 장면에 사용자가 직접 동영상을 올릴 수도 있다 — 실제 촬영 영상이 있으면 그게 제일 낫다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestIntroScript, requestNarration, generateClip, previewClipPrompt, NARRATION_VOICES, SCRIPT_TONES, SCRIPT_FORMATS, HERO_OPTIONS, VEO_TIERS, veoUsdPerSec, usageLog, resetUsageLog, summarizeUsage } from '../services/ai/videoClient';
import { isAiConfigured } from '../services/ai/geminiClient';
import {
  ASPECTS,
  prepareProject,
  createPreview,
  encodeProject,
  releaseProject,
  isVideoBuildSupported,
  pcm16Base64ToFloat32,
  floatToAudioBuffer,
  renderSceneThumbnail,
  makeThumbnailBase64,
  makeFirstFrameBase64,
} from '../services/video/introVideoBuilder';
import { decodeAudioFile } from '../services/video/audioTools';
import { clipCacheKey, getCachedClip, putCachedClip } from '../services/video/clipCache';
import { createNewProductContent, uploadContentFile, saveProductContentInfo, getProductCity, getUserCompanyInfo } from '../services/apiService';
import { esc } from '../services/detailImage/detailImageRenderer';
import { getUserInfo } from '../utils/tokenManager';

const S3_PUBLIC_PREFIX = 'https://kr.object.iwinv.kr/orgin/1/';
const DURATIONS = [15, 30, 60, 90];
const NARRATION_RATES = [
  { value: 1, label: '보통' },
  { value: 1.1, label: '조금 빠르게' },
  { value: 1.2, label: '빠르게' },
];
const MUSIC_MODES = [
  { id: 'auto', label: '자동 생성 (톤에 맞춤)' },
  { id: 'file', label: '내 음악 파일' },
  { id: 'none', label: '없음' },
];
/** AI 에게 보여줄 사진 수 상한 (서버 MAX_PHOTOS 와 같게) */
const MAX_PHOTOS = 40;

/** HTML 을 한 줄 텍스트로 (일정내용·콘텐츠는 에디터 HTML 로 저장돼 있다) */
function htmlToText(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

/** 일정 카드 행의 항공편 JSON 을 "편명 출발→도착 시각" 한 줄로 */
function flightLineFromRow(row) {
  try {
    if (!row?.항공편) return '';
    const arr = JSON.parse(row.항공편);
    const f = Array.isArray(arr) ? arr[0] : null;
    if (!f) return '';
    const dep = f.출발공항명 || f.출발공항 || f.출발공항코드 || '';
    const arr2 = f.도착공항명 || f.도착공항 || f.도착공항코드 || '';
    return [f.편명, dep && arr2 ? `${dep}→${arr2}` : '', f.출발시간 ? `${f.출발시간} 출발` : ''].filter(Boolean).join(' ');
  } catch { return ''; }
}

/** 대본 근거로 보낼 상세정보 글의 상한 (서버 describeProduct 와 같게) */
const MAX_EXTRAS = 12;
const MAX_EXTRA_CHARS = 1500;
const MAX_EXTRAS_TOTAL_CHARS = 8000;

/** 콘텐츠 항목에서 본문 HTML(없으면 긴 텍스트)을 찾는다. 상세설정 화면의 extractHtmlCandidate 와 같은 기준. */
function contentItemBody(item) {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return '';
  const keys = ['테그', '태그', 'html', 'HTML', '내용', 'content', 'body', 'desc', 'description', 'text'];
  let fallback = '';
  for (const k of keys) {
    const v = item[k];
    if (typeof v !== 'string') continue;
    if (/<[^>]+>/.test(v)) return v;
    if (!fallback && v.trim()) fallback = v;
  }
  // 이름 모를 키에 HTML 이 들어 있는 경우
  for (const v of Object.values(item)) {
    if (typeof v === 'string' && /<[^>]+>/.test(v)) return v;
  }
  return fallback;
}

/**
 * 콘텐츠 목록(상세정보)에서 대본 근거가 될 글을 뽑는다 — 상품특징·포함/불포함·특전·호텔·식사·예약안내 등.
 * contentMain 은 [{ 코드, 코드명, 컨텐츠: JSON 문자열 }] 이고 컨텐츠 안에 항목들이 있다.
 * 일정소개 섹션은 일정표 HTML 이라 제외한다 (일정은 days 로 따로 보낸다).
 *
 * 옵션(기본값은 소개영상 기준): max 항목 수, maxChars 항목당 글자, totalChars 전체 글자, withArea 가 true 면
 * 항목마다 area(구역명 = 코드명)·item(항목명)을 같이 넣는다 — 상세 이미지가 구역별(판매정보·유의사항 등)로 옮겨 적을 때 쓴다.
 */
export function extractContentExtras(contentMain, { max = MAX_EXTRAS, maxChars = MAX_EXTRA_CHARS, totalChars = MAX_EXTRAS_TOTAL_CHARS, withArea = false } = {}) {
  const titleKeys = ['컨텐츠명', '콘텐츠명', '항목명', '제목', '타이틀', '이름', '명', 'title', 'name'];
  const skipSection = /일정소개|일정표/;
  const hot = /특징|포함|불포함|특전|혜택|추천|포인트|하이라이트|소개|호텔|숙소|식사|쇼핑|가이드|인솔/;
  const out = [];
  for (const row of contentMain || []) {
    const section = String(row?.코드명 || '').trim();
    if (skipSection.test(section)) continue;
    let items = [];
    try { items = JSON.parse(row?.컨텐츠 || '[]'); } catch { /* 형식이 다르면 건너뛴다 */ }
    if (!Array.isArray(items)) continue;
    for (const it of items) {
      if (!it) continue;
      const tk = typeof it === 'object' ? titleKeys.find((k) => typeof it[k] === 'string' && it[k].trim()) : null;
      const itemTitle = tk ? it[tk].trim() : '';
      // "포함사항 · 항공" 처럼 섹션과 항목 제목을 같이 적어 AI 가 글의 성격을 알게 한다
      const title = section && itemTitle && section !== itemTitle ? `${section} · ${itemTitle}` : (itemTitle || section);
      const text = htmlToText(contentItemBody(it)).slice(0, maxChars);
      if (text.length < 10) continue;
      out.push({ title, text, hot: hot.test(title), area: section, item: itemTitle, order: out.length });
    }
  }
  // 매력 포인트가 될 만한 글부터(같은 순위끼리는 화면 순서 — 구역 안의 순서가 흔들리지 않게 order 로 고정), 총 글자 수 상한 안에서 최대 max 개
  const picked = [];
  let total = 0;
  for (const e of out.sort((a, b) => Number(b.hot) - Number(a.hot) || a.order - b.order)) {
    if (picked.length >= max || total + e.text.length > totalChars) continue;
    total += e.text.length;
    picked.push(withArea ? { title: e.title, text: e.text, area: e.area, item: e.item } : { title: e.title, text: e.text });
  }
  return picked;
}

/**
 * 상세설정 화면의 상태를 erp-video 가 받는 product 모양으로 바꾼다.
 */
/** 일정표 편집의 "주요 관광지 방문순서" (table4.방문지키워드, 쉼표 구분) → 이름 배열 */
export function visitOrderOf(itineraryData) {
  const t4 = Array.isArray(itineraryData?.table4) ? itineraryData.table4 : [];
  return String(t4?.[0]?.방문지키워드 || '').split(',').map((x) => x.trim()).filter(Boolean);
}

export function buildProductPayload({ productName, cityName, itineraryData, contentMain, extras, useVisitOrder = true }) {
  const t1 = Array.isArray(itineraryData?.table1) ? itineraryData.table1 : [];
  const t3 = Array.isArray(itineraryData?.table3) ? itineraryData.table3 : [];
  const t5 = Array.isArray(itineraryData?.table5) ? itineraryData.table5 : [];
  const dayNos = new Set();
  for (const r of [...t1, ...t3, ...t5]) { const n = Number(r?.일차); if (n > 0) dayNos.add(n); }
  const days = Array.from(dayNos).sort((a, b) => a - b).map((day) => ({
    day,
    places: t1.filter((r) => Number(r?.일차) === day).map((r) => ({
      name: String(r?.일정명 || '').trim(),
      desc: htmlToText(r?.일정내용 || r?.일정설명 || '').slice(0, 600),
      flight: flightLineFromRow(r),
    })).filter((p) => p.name || p.desc || p.flight),
    meals: String(t3.find((r) => Number(r?.일차) === day)?.식사정보 || '').trim(),
    hotels: t5.filter((r) => Number(r?.일차) === day).map((r) => String(r?.일정명 || '').trim()).filter(Boolean),
  }));
  return {
    name: String(productName || '').trim(),
    cityName: String(cityName || '').trim(),
    // 방문순서: 대본의 방문지 힌트 + 경로 지도의 점 순서. 끄면 빈 문자열(서버는 일차별 대표 장소로 경로를 시도한다)
    keywords: useVisitOrder ? visitOrderOf(itineraryData).join(', ') : '',
    days,
    extras: Array.isArray(extras) ? extras : extractContentExtras(contentMain),
  };
}

/**
 * AI 에게 보여줄 사진 후보. 대표이미지 + 일정 카드 사진. [{ id, url, hiUrl, source, day }]
 *   url   — 화면·AI 썸네일용 (이미지경로2 같은 축소본, 가볍다)
 *   hiUrl — 영상에 실제로 그릴 원본. 화면용 축소본을 1280px 로 키우면 흐려지기 때문에 원본을 따로 든다.
 * 카드 사진은 날짜별로 돌아가며 뽑아서 특정 날에 몰리지 않게 한다.
 */
export function collectPhotoCandidates({ itineraryData, cardImagesByPlace, cardImagesNoPlace, cardImageOriginals, repImages }) {
  const seen = new Set();
  const out = [];
  const push = (url, hiUrl, source, day) => {
    if (!url || seen.has(url) || out.length >= MAX_PHOTOS) return;
    seen.add(url);
    out.push({ id: out.length + 1, url, hiUrl: hiUrl || url, source, day });
  };
  for (const img of (repImages || []).slice(0, 10)) {
    const small = img?.이미지경로2 || img?.이미지경로;
    push(small, img?.원본이미지경로 || img?.이미지경로 || small, '대표이미지', 0);
  }
  const hiOf = (u) => cardImageOriginals?.[u] || u;

  const t1 = Array.isArray(itineraryData?.table1) ? itineraryData.table1 : [];
  // 카드별 사진 목록 (장소명 붙여서)
  const perCard = t1.map((r) => {
    const no = Number(r?.상품일정번호 ?? r?.일정번호 ?? r?.장소번호 ?? 0);
    const day = Number(r?.일차) || 0;
    const name = String(r?.일정명 || '').trim();
    const byPlace = cardImagesByPlace?.[no] || {};
    const list = [];
    for (const [place, urls] of Object.entries(byPlace)) for (const u of urls) list.push({ url: u, hiUrl: hiOf(u), source: `${day}일차 · ${place || name}` });
    for (const u of cardImagesNoPlace?.[no] || []) list.push({ url: u, hiUrl: hiOf(u), source: `${day}일차 · ${name}` });
    return { day, list };
  }).filter((c) => c.list.length);
  // 라운드로빈: 카드마다 한 장씩 돌아가며
  for (let round = 0; round < 12 && out.length < MAX_PHOTOS; round++) {
    let any = false;
    for (const c of perCard) {
      const item = c.list[round];
      if (!item) continue;
      any = true;
      push(item.url, item.hiUrl, item.source, c.day);
    }
    if (!any) break;
  }
  return out;
}

/** AI 가 사진을 안 골랐을 때의 기계적 선택: 그 날 카드 사진 → 대표이미지 */
function fallbackSceneImages({ scene, sceneIdx, candidates }) {
  const dayNumbers = Array.isArray(scene.dayNumbers) ? scene.dayNumbers.map(Number).filter((n) => n > 0) : [];
  const picked = [];
  if (dayNumbers.length) {
    for (const c of candidates) {
      if (dayNumbers.includes(c.day) && !picked.includes(c.url)) picked.push(c.url);
      if (picked.length >= 3) break;
    }
  }
  if (!picked.length) {
    const rep = candidates.filter((c) => c.day === 0);
    const start = rep.length ? (sceneIdx * 2) % rep.length : 0;
    for (let i = 0; i < rep.length && picked.length < 2; i++) picked.push(rep[(start + i) % rep.length].url);
  }
  return picked;
}

const STAGE_LABEL = {
  veo: 'AI 영상 클립 생성 중 (동시에 2개씩, 클립당 1~3분)',
  tts: '나레이션 음성 만드는 중',
  prepare: '사진 불러오는 중',
  music: '배경음악 만드는 중',
  encode: '영상 인코딩 중',
};

// ── AI 영상(Veo) 계획 ────────────────────────────────────────────

/** 어느 장면을 AI 영상으로 만들지. 사용자가 올린 동영상이 있으면 그게 우선, 장면별 체크(veo)가 있으면 그것, 없으면 모드대로. */
const VEO_MODES = [
  { id: 'off', label: '끄기 (사진만)' },
  { id: 'key', label: '핵심 장면만 (인트로·특징)' },
  { id: 'all', label: '모든 장면' },
];
const VEO_SECS = [4, 6, 8];
/**
 * 동시에 돌리는 Veo 작업 수와 시작 간격. Veo 한도는 모델별 분당 4건·하루 50건(2026-09, 유료 Tier 1) — 3개만 몰려도 429 가 났다.
 * 시작 간격 15초면 즉시 실패(400)가 연달아 나도 분당 4건을 넘지 않는다. 정상 생성은 클립당 1~3분이라 실제로는 훨씬 느슨하다.
 */
const VEO_CONCURRENCY = 2;
const VEO_START_GAP_MS = 15000;
/** 나레이션 글자 수 → 초 (서버 planLength 의 5~6자/초, 속도 1.1 배 기준) */
const secsOfNarration = (text, rate = 1.1) => String(text || '').replace(/\s/g, '').length / (5.5 * rate) + 0.45;

/** 클립 방식. 대본의 shotMode 가 기본이고 장면 카드에서 바꿀 수 있다. 사진이 없으면 story 만 가능하다. */
const SHOT_MODES = [
  { id: 'photo', label: '사진 움직이기', hint: '사진을 그대로 살려 카메라만 움직입니다. 실제 장소가 가장 정확합니다.' },
  { id: 'action', label: '사진 속 연출', hint: '사진의 장소는 그대로 두고 사람의 행동(동전 던지기·걷기·사진 찍기)을 더합니다.' },
  { id: 'story', label: '새 장면 생성', hint: '나레이션의 장면(기차 창밖·인솔자 따라 걷기·전용차량·식사)을 새로 그립니다. Fast/Standard 는 장면 첫 사진을 참고해 비슷한 장소·분위기로 만들고(8초 고정), Lite 는 글로만 만듭니다. 실제와 다른 모습이 나올 수 있습니다.' },
];
function shotModeOf(scene) {
  const hasPhoto = (scene.imageUrls || []).filter(Boolean).length > 0;
  const m = SHOT_MODES.some((x) => x.id === scene.shotMode) ? scene.shotMode : (hasPhoto ? 'photo' : 'story');
  return hasPhoto ? m : 'story';
}

function sceneVeoEnabled(scene, kind, mode) {
  if (mode === 'off' || scene.userClips?.length) return false;
  if (typeof scene.veo === 'boolean') return scene.veo;
  if (mode === 'all') return kind !== 'route';
  if (mode === 'key') return kind === 'intro' || kind === 'feature';
  return false;
}

/** 예능형 동행 설정을 캐릭터 시트 뒤에 붙인다 — Veo 는 시트만 보므로 "혼자" 를 여기서 말해 줘야 일행·인솔자가 안 생긴다 */
const COMPANION_CLAUSE = {
  solo: 'Travels alone — no companions, no group, no tour leader; other people only as distant background.',
  couple: 'Travels with a partner (Korean, similar age) who appears beside the protagonist but never takes the focus.',
  friends: 'Travels with one or two Korean friends of similar age who stay in the background of shots.',
  family: 'Travels with family (Korean) who appear around the protagonist but never take the focus.',
};
const heroCharacter = (look, companion) => (String(look || '').trim() ? `${String(look).trim()}. ${COMPANION_CLAUSE[companion] || COMPANION_CLAUSE.solo}` : '');

/** shot 이 비었을 때의 지시(photo 모드용 카메라 움직임). 장면마다 다르게 돌아가며 준다. */
const DEFAULT_SHOTS = [
  'slow cinematic dolly-in, warm natural light, subtle movement in the scene',
  'gentle lateral pan from left to right, soft daylight, clouds drifting slowly',
  'slow pull-back revealing the whole scene, golden hour glow',
  'subtle upward drift, clear sky, light breeze moving leaves and water',
];
const defaultShot = (idx) => DEFAULT_SHOTS[idx % DEFAULT_SHOTS.length];

/**
 * 한 장면에 만들 클립 목록. 장면 길이(나레이션)에 맞춰 4/6/8초 중 고르고, 2컷이면 나눠 준다
 * (photo/action 은 사진 두 장에, story 는 같은 장면의 다른 앵글로).
 * @returns {Array<{ mode:string, imageUrl:string|null, description:string, durationSeconds:number }>}
 */
function planSceneClips(scene, idx, { clipsPerScene, narrationRate, tier, character, heroImageName }) {
  const est = Math.max(2.5, secsOfNarration(scene.narration, narrationRate));
  const mode = shotModeOf(scene);
  const photos = (scene.imageUrls || []).filter(Boolean);
  const images = mode === 'story' ? [] : photos;
  // 새 장면 생성: 장면 첫 사진을 참고 이미지로 보내 비슷한 장소·분위기를 잡는다 (lite 는 미지원). 참고 이미지를 쓰면 Veo 가 8초만 받는다.
  const referenceUrl = mode === 'story' && tier !== 'lite' ? photos[0] || null : null;
  // 예능형 주인공 사진도 참고 이미지 (story·lite 아닐 때). 경로 장면(shot 없음)에는 주인공이 안 나오므로 붙이지 않는다
  const heroRef = mode === 'story' && tier !== 'lite' && heroImageName ? heroImageName : null;
  const n = Math.max(1, Math.min(clipsPerScene, mode === 'story' ? clipsPerScene : Math.max(1, images.length)));
  const per = est / n;
  const durationSeconds = referenceUrl || heroRef ? 8 : (VEO_SECS.find((d) => d >= per) ?? 8);
  const shot = String(scene.shot || '').trim() || defaultShot(idx);
  return Array.from({ length: n }, (_, k) => ({
    mode,
    imageUrl: images[k] || null,
    referenceUrl,
    character: character || undefined,
    heroImageName: heroRef || undefined,
    // 사용자가 생성 전 확인 창에서 고친 최종 프롬프트 (컷별). 있으면 서버 조립 대신 그대로 보낸다
    prompt: String(scene.promptOverrides?.[k] || '').trim() || undefined,
    description: k === 0 ? shot : `${shot}. A different angle of the same moment: ${defaultShot(idx + k)}`,
    descriptionKo: k === 0 ? String(scene.shotKo || '').trim() : `${String(scene.shotKo || '').trim()} (같은 순간의 다른 앵글)`,
    durationSeconds,
  }));
}

/** 첫 장면(인트로)과 마지막 장면(마무리)은 가운데 큰 제목, 나머지는 하단 자막 */
function sceneAlign(i, count) {
  return i === 0 || (count > 1 && i === count - 1) ? 'center' : 'bottom';
}

/** 장면 종류. 조립기가 전환·레이아웃(특징 = 콜라주 + 포인트 칩)을 이걸로 정한다. 서버 isFeatureScene 과 같은 기준. */
function sceneKind(i, count, s) {
  if (i === 0) return 'intro';
  if (count > 1 && i === count - 1) return 'closing';
  if (/경로|루트|이동/.test(String(s?.label || ''))) return 'route';
  const days = (Array.isArray(s?.dayNumbers) ? s.dayNumbers : []).filter((n) => Number(n) > 0);
  return days.length === 0 || /특징|포인트|장점/.test(String(s?.label || '')) ? 'feature' : 'day';
}

/**
 * 조립기에 넘길 장면 목록. 종류를 붙이고, 특징 장면이 여럿이면 keyPoints 를 나눠 준다 (첫 장면에 앞쪽 포인트).
 */
function buildBuilderScenes(script, extra = () => ({})) {
  const count = script.scenes.length;
  const kinds = script.scenes.map((s, i) => sceneKind(i, count, s));
  const featureIdx = kinds.map((k, i) => (k === 'feature' ? i : -1)).filter((i) => i >= 0);
  const points = Array.isArray(script.keyPoints) ? script.keyPoints : [];
  const per = featureIdx.length ? Math.ceil(points.length / featureIdx.length) : 0;
  return script.scenes.map((s, i) => {
    const f = featureIdx.indexOf(i);
    return {
      ...s,
      kind: kinds[i],
      align: sceneAlign(i, count),
      points: f >= 0 ? points.slice(f * per, (f + 1) * per) : [],
      ...extra(s, i),
    };
  });
}

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function AiIntroVideoModal({
  open,
  onClose,
  productId,
  productName,
  itineraryData,
  cardImagesByPlace,
  cardImagesNoPlace,
  cardImageOriginals, // 화면용 축소본 URL → 원본 URL
  repImages,
  contentMain, // 콘텐츠 목록 [{ 코드, 코드명, 컨텐츠 }] — 종류 선택 + 대본 근거 글 추출에 쓴다
  googleMapsKey, // 경로 지도 배경(Google Static Maps)용. 없으면 점·선만 그리는 약도
  onAttached, // 콘텐츠 저장 후 목록 새로고침용
}) {
  // AI 영상(Veo) 고급 기능은 업체번호 1번에만 오픈
  const { companyNo } = getUserInfo() || {};
  const canUseVeo = String(companyNo || '') === '1';

  const [step, setStep] = useState('script'); // 'script' | 'working' | 'preview' | 'done'
  const [scriptLoading, setScriptLoading] = useState(false);
  const [error, setError] = useState('');
  const [script, setScript] = useState(null); // { title, subtitle, closing, scenes:[{ label, dayNumbers, title, caption, narration, imageUrls }] }
  const [thumbs, setThumbs] = useState({}); // sceneIdx -> dataURL
  const [progress, setProgress] = useState(null); // { stage, done, total, label? }
  const [results, setResults] = useState([]); // [{ aspect, blob, url, durationSec, audioCodec, width, height }]
  const [attachTypeCode, setAttachTypeCode] = useState('');
  const [attachTitle, setAttachTitle] = useState('AI 여행일정 소개 영상');
  const [attaching, setAttaching] = useState(false);
  const [imagePickerFor, setImagePickerFor] = useState(null);
  // 설정
  const [tone, setTone] = useState('calm');
  const [scriptFormat, setScriptFormat] = useState('promo'); // 'promo' 소개형 | 'story' 체험형(1인칭) | 'show' 예능형(주인공 3인칭)
  // 예능형 주인공. look(영어 캐릭터 시트)은 대본과 함께 AI 가 정하고 사용자가 고칠 수 있다(script.hero). 사진은 선택 — Fast/Standard 에서 참고 이미지로 간다
  const [hero, setHero] = useState({ gender: 'female', age: '30s', companion: 'solo' });
  const [heroPhoto, setHeroPhoto] = useState(null); // { name, blob, url }
  const heroInputRef = useRef(null);
  const [targetSec, setTargetSec] = useState(30);
  const [voice, setVoice] = useState(NARRATION_VOICES[0].id);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [aspects, setAspects] = useState(['16:9']);
  const [contact, setContact] = useState('');
  const [aiPickPhotos, setAiPickPhotos] = useState(true);
  // 대본 근거가 되는 상세정보 글. 화면에 보여 주고, 필요 없는 글(유의사항 등)은 빼고 보낼 수 있다.
  const extrasAll = useMemo(() => extractContentExtras(contentMain), [contentMain]);
  const [extrasOff, setExtrasOff] = useState(() => new Set()); // 뺀 항목의 index
  // 주요 관광지 방문순서 — 대본 힌트이자 경로 지도의 점 순서. 보여 주고 끌 수 있게 한다.
  const visitOrder = useMemo(() => visitOrderOf(itineraryData), [itineraryData]);
  const [useVisitOrder, setUseVisitOrder] = useState(true);
  // 토큰 사용량: 호출이 끝날 때마다 usageLog(모듈 전역)를 다시 합산해 상태로 든다
  const [usage, setUsage] = useState(() => summarizeUsage([]));
  const refreshUsage = useCallback(() => setUsage(summarizeUsage(usageLog)), []);
  // 소리
  const [narrationRate, setNarrationRate] = useState(1.1);
  const [musicMode, setMusicMode] = useState('auto');
  const [musicGain, setMusicGain] = useState(1); // 0~1.5, 슬라이더
  const [musicFile, setMusicFile] = useState(null); // { name, samples }
  const [sfx, setSfx] = useState(true);
  const [musicDuck, setMusicDuck] = useState(true); // 나레이션 중 음악 낮추기(덕킹)
  const musicInputRef = useRef(null);
  // 고급: AI 영상(Veo). 클립은 세션 안에서 키 → { blob, url, seconds } 로 들고, IndexedDB 에도 둔다
  const [veoMode, setVeoMode] = useState('off');
  const [veoTier, setVeoTier] = useState('fast');
  const [veoClipsPerScene, setVeoClipsPerScene] = useState(1);
  const veoClipsRef = useRef(new Map());
  const [veoReady, setVeoReady] = useState(0); // 클립이 준비될 때마다 +1 — 썸네일·"준비됨" 표시 갱신용
  const [veoElapsed, setVeoElapsed] = useState(0);
  const [veoFailed, setVeoFailed] = useState([]); // [{ idx, error }] 마지막 준비에서 실패해 사진으로 대신한 장면
  // 생성 전 확인 창: 만들 클립마다 최종 프롬프트를 보여 주고 고칠 수 있게 한다. resolve(items|null) 로 ensureVeoClips 가 이어진다
  const [veoReview, setVeoReview] = useState(null); // { family, tierLabel, items:[{ idx, k, clip, assembled, text }], resolve }
  const clipInputFor = useRef(null); // 동영상 올리기 대상 장면 index
  const clipInputRef = useRef(null);
  // 미리보기
  const [previewAspect, setPreviewAspect] = useState('16:9');
  const [previewTime, setPreviewTime] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewDuration, setPreviewDuration] = useState(0);
  const [previewMissing, setPreviewMissing] = useState(0);
  const [previewMissingClips, setPreviewMissingClips] = useState(0); // 브라우저가 못 여는 동영상(.mov 등)은 사진으로 대신한다

  const abortRef = useRef(null);
  const audioCtxRef = useRef(null);
  const previewSourceRef = useRef(null);
  const [previewingIdx, setPreviewingIdx] = useState(null);
  const imageCacheRef = useRef(new Map()); // url → ImageBitmap
  const ttsCacheRef = useRef(new Map()); // `${voice}||${text}` → { samples, sampleRate }
  const projectsRef = useRef({}); // aspect → project (미리보기·인코딩 공용)
  const projectKeyRef = useRef(''); // 프로젝트를 만든 재료의 서명 — 바뀌면 다시 준비
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const cityRef = useRef('');

  const supported = isVideoBuildSupported();
  const candidates = useMemo(
    () => collectPhotoCandidates({ itineraryData, cardImagesByPlace, cardImagesNoPlace, cardImageOriginals, repImages }),
    [itineraryData, cardImagesByPlace, cardImagesNoPlace, cardImageOriginals, repImages],
  );
  // 영상에 그릴 때는 원본 URL 로 바꾼다 (대본·UI 는 가벼운 축소본 URL 을 든다)
  const hiResOf = useCallback((u) => candidates.find((c) => c.url === u)?.hiUrl || u, [candidates]);
  const contentTypes = useMemo(
    () => (contentMain || []).map((row) => ({ code: String(row?.코드 || ''), name: String(row?.코드명 || '') })).filter((t) => t.code),
    [contentMain],
  );

  const stopPreviewAudio = useCallback(() => {
    try { previewSourceRef.current?.stop(); } catch { /* 이미 멈춤 */ }
    previewSourceRef.current = null;
    setPreviewingIdx(null);
  }, []);

  const dropProjects = useCallback(() => {
    for (const p of Object.values(projectsRef.current)) releaseProject(p);
    projectsRef.current = {};
    projectKeyRef.current = '';
  }, []);

  // 모달이 열릴 때 초기화. 닫히면 진행 중이던 작업을 중단한다.
  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      setVeoReview((r) => { r?.resolve(null); return null; });
      stopPreviewAudio();
      playerRef.current?.destroy();
      playerRef.current = null;
      dropProjects();
      return;
    }
    setStep('script');
    setError('');
    setScript((prev) => { for (const s of prev?.scenes || []) for (const c of s.userClips || []) URL.revokeObjectURL(c.url); return null; });
    setThumbs({});
    resetUsageLog();
    setUsage(summarizeUsage([]));
    setProgress(null);
    setResults((prev) => { prev.forEach((r) => URL.revokeObjectURL(r.url)); return []; });
    setAttachTypeCode(contentTypes?.[0]?.code || '');
    setAttachTitle('AI 여행일정 소개 영상');
    setVeoMode('off');
    setVeoFailed([]);
    setVeoElapsed(0);
    setAspects(['16:9']);
    setPreviewAspect('16:9');
    // 마무리 화면 연락처: 회사 정보에서 채워 두고 사용자가 고칠 수 있게
    getUserCompanyInfo().then((c) => {
      if (!c) return;
      const phone = ['대표전화', '전화번호', '업체전화', '연락처', '휴대폰'].map((k) => c[k]).find((v) => typeof v === 'string' && v.trim());
      setContact([c.업체명, phone].filter(Boolean).join(' · '));
    }).catch(() => {});
  }, [open]);

  const totalNarrationChars = useMemo(
    () => (script?.scenes || []).reduce((n, s) => n + String(s.narration || '').replace(/\s/g, '').length, 0),
    [script],
  );

  // ── 1) 대본 ────────────────────────────────────────────────────
  const generateScript = useCallback(async () => {
    setError('');
    setScriptLoading(true);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      if (!cityRef.current) {
        const city = await getProductCity({ productNo: productId }).catch(() => null);
        cityRef.current = city?.도시명 || city?.코드명 || '';
      }
      const extras = extrasAll.filter((_, i) => !extrasOff.has(i));
      const product = buildProductPayload({ productName, cityName: cityRef.current, itineraryData, contentMain, extras, useVisitOrder });
      if (!product.days.length) throw new Error('일정표에 등록된 일정이 없습니다. 일정을 먼저 등록해 주세요.');
      // 무엇을 근거로 썼는지 확인할 수 있게 남긴다 (상세정보 글이 대본에 안 보일 때 첫 확인 지점)
      console.debug('[AI 소개영상] 대본 근거', { days: product.days.length, visitOrder: product.keywords, extras: extras.map((e) => `${e.title} (${e.text.length}자)`) });

      // 사진 썸네일 — AI 가 보고 고르게. 실패한 사진은 빼고 보낸다.
      let photos = [];
      if (aiPickPhotos && candidates.length) {
        setProgress({ stage: 'photos', done: 0, total: candidates.length });
        let done = 0;
        photos = (await Promise.all(candidates.map(async (c) => {
          const data = await makeThumbnailBase64(c.url, imageCacheRef.current).catch(() => null);
          done++;
          setProgress({ stage: 'photos', done, total: candidates.length });
          return data ? { id: c.id, source: c.source, data } : null;
        }))).filter(Boolean);
        setProgress(null);
      }
      if (ac.signal.aborted) return;

      const raw = await requestIntroScript({ product, targetSec, tone, format: scriptFormat, hero: scriptFormat === 'show' ? hero : undefined, photos, signal: ac.signal });
      refreshUsage();
      const byId = new Map(candidates.map((c) => [c.id, c.url]));
      const used = new Set();
      const scenes = (raw.scenes || []).map((s, i) => {
        const aiUrls = (Array.isArray(s.photoIds) ? s.photoIds : []).map((id) => byId.get(Number(id))).filter((u) => u && !used.has(u)).slice(0, 3);
        aiUrls.forEach((u) => used.add(u));
        return {
          label: s.label || '',
          dayNumbers: Array.isArray(s.dayNumbers) ? s.dayNumbers : [],
          title: s.title || '',
          caption: s.caption || '',
          narration: s.narration || '',
          shot: String(s.shot || '').trim().slice(0, 300),
          shotMode: SHOT_MODES.some((m) => m.id === s.shotMode) ? s.shotMode : undefined,
          shotKo: String(s.shotKo || '').trim().slice(0, 300), // shot 의 한국어 대역(읽기용)
          imageUrls: aiUrls.length ? aiUrls : fallbackSceneImages({ scene: s, sceneIdx: i, candidates }),
        };
      });
      // 인트로는 상품 제목/부제를 그대로 크게, 마무리는 마무리 문구 + 연락처
      if (scenes.length) {
        scenes[0] = { ...scenes[0], title: raw.title || scenes[0].title, caption: raw.subtitle || scenes[0].caption };
        const last = scenes.length - 1;
        if (last > 0) scenes[last] = { ...scenes[last], title: raw.closing || scenes[last].title, caption: contact || scenes[last].caption };
      }
      // keyPoints: AI 가 상세정보 글에서 뽑은 "이 상품을 고를 이유". 나레이션에 실제로 들어갔는지 확인용으로 보여 준다.
      const keyPoints = (Array.isArray(raw.keyPoints) ? raw.keyPoints : []).map((k) => String(k || '').trim()).filter(Boolean);
      // route: 방문 순서 + 좌표 (서버가 정리해서 줌). 경로 장면·일차 미니맵 전환의 재료
      const route = (Array.isArray(raw.route) ? raw.route : []).filter((r) => r && Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng)))
        .map((r) => ({ name: String(r.name || '').trim(), lat: Number(r.lat), lng: Number(r.lng), day: Number(r.day) || 0 }));
      setScript((prev) => {
        for (const s of prev?.scenes || []) for (const c of s.userClips || []) URL.revokeObjectURL(c.url);
        const heroOut = scriptFormat === 'show' && raw.hero ? { look: String(raw.hero.look || '').trim() } : null;
        return { title: raw.title || '', subtitle: raw.subtitle || '', closing: raw.closing || '', keyPoints, route, scenes, hero: heroOut };
      });
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setScriptLoading(false);
      setProgress(null);
    }
  }, [productId, productName, itineraryData, contentMain, extrasAll, extrasOff, useVisitOrder, candidates, aiPickPhotos, targetSec, tone, scriptFormat, hero, contact, refreshUsage]);

  // 연락처가 늦게 도착하면 마무리 캡션에 반영 (사용자가 이미 적었으면 두지 않는다)
  useEffect(() => {
    if (!contact) return;
    setScript((prev) => {
      if (!prev || prev.scenes.length < 2) return prev;
      const last = prev.scenes.length - 1;
      if (prev.scenes[last].caption) return prev;
      return { ...prev, scenes: prev.scenes.map((s, i) => (i === last ? { ...s, caption: contact } : s)) };
    });
  }, [contact]);

  // ── AI 영상 클립 계획 (견적·썸네일·프로젝트가 같은 계획을 본다) ──
  /** 장면별 클립 계획. [{ idx, kind, clips:[{ imageUrl, description, durationSeconds }] }] — AI 영상을 쓰는 장면만 */
  const veoPlan = useMemo(() => {
    if (!canUseVeo || !script) return [];
    return buildBuilderScenes(script)
      .map((s, idx) => ({ idx, kind: s.kind, enabled: sceneVeoEnabled(s, s.kind, veoMode), scene: s }))
      .filter((x) => x.enabled)
      .map((x) => ({ idx: x.idx, kind: x.kind, clips: planSceneClips(x.scene, x.idx, { clipsPerScene: veoClipsPerScene, narrationRate, tier: veoTier, character: x.kind === 'route' ? '' : heroCharacter(script.hero?.look, hero.companion), heroImageName: x.kind === 'route' ? '' : heroPhoto?.name || '' }) }));
  }, [canUseVeo, script, veoMode, veoClipsPerScene, narrationRate, veoTier, heroPhoto, hero.companion]);
  /** 출력 형식 → Veo 비율. 1:1 은 가로 클립을 가운데 잘라 쓴다 (Veo 는 16:9 / 9:16 만 만든다) */
  const veoFamilyOf = (aspect) => (aspect === '9:16' ? '9:16' : '16:9');
  const veoFamilies = useMemo(() => Array.from(new Set(aspects.map(veoFamilyOf))), [aspects]);
  const veoKeyOf = useCallback((clip, family, durationSeconds = clip.durationSeconds) => clipCacheKey({ tier: veoTier, aspectRatio: family, durationSeconds, mode: clip.mode, imageUrl: clip.imageUrl, referenceUrl: clip.referenceUrl, description: clip.description, prompt: clip.prompt, character: clip.character, heroImageName: clip.heroImageName }), [veoTier]);
  /**
   * 이미 만든 클립의 키. 길이는 나레이션 글자 수로 어림한 값이라 문장을 한 글자만 고쳐도 6초→8초로 바뀔 수 있는데,
   * 그때마다 다시 사면 안 된다 — 같은 사진·지시·등급·비율이면 길이가 달라도 있는 클립을 쓴다(짧으면 마지막 프레임에서 멈춘다).
   */
  const veoReadyKey = useCallback((clip, family) => {
    for (const d of [clip.durationSeconds, ...VEO_SECS]) { const k = veoKeyOf(clip, family, d); if (veoClipsRef.current.has(k)) return k; }
    return null;
  }, [veoKeyOf]);
  const veoEstimate = useMemo(() => {
    void veoReady; // veoClipsRef(캐시)는 ref 라 바뀌어도 재계산되지 않는다 — 클립이 준비될 때 올라가는 이 카운터로 다시 센다
    const clips = veoPlan.reduce((n, p) => n + p.clips.length, 0);
    const seconds = veoPlan.reduce((n, p) => n + p.clips.reduce((m, c) => m + c.durationSeconds, 0), 0);
    let cached = 0;
    for (const pl of veoPlan) for (const c of pl.clips) for (const f of veoFamilies) if (veoReadyKey(c, f)) cached += c.durationSeconds;
    const total = seconds * veoFamilies.length;
    return { clips: clips * veoFamilies.length, seconds: total, usd: total * veoUsdPerSec(veoTier), cachedSeconds: cached, usdRemaining: (total - cached) * veoUsdPerSec(veoTier) };
  }, [veoPlan, veoFamilies, veoTier, veoReadyKey, veoReady]);

  /** 장면에 깔 클립 Blob 들: 사용자 동영상 > 준비된 AI 클립 > 없음(사진) */
  const sceneClipBlobs = useCallback((s, idx, family) => {
    if (s.userClips?.length) return s.userClips.map((c) => c.blob);
    const plan = veoPlan.find((p) => p.idx === idx);
    if (!plan) return [];
    return plan.clips.map((c) => { const k = veoReadyKey(c, family); return k ? veoClipsRef.current.get(k).blob : null; }).filter(Boolean);
  }, [veoPlan, veoReadyKey]);

  // 장면 썸네일. 화면에 보이는 값(라벨·제목·캡션·사진·클립)이 바뀔 때만 다시 그린다 — 입력이 멈춘 뒤 300ms 후.
  const thumbKey = useMemo(
    () => (script && step === 'script' ? JSON.stringify([script.title, tone, script.keyPoints, script.route, veoMode, veoTier, veoClipsPerScene, narrationRate, veoReady, veoFamilies[0], script.hero?.look, heroPhoto?.name, script.scenes.map((s) => [s.label, s.title, s.caption, s.imageUrls, s.veo, s.shot, s.shotMode, s.promptOverrides, (s.userClips || []).map((c) => c.name)])]) : ''),
    [script, step, tone, veoMode, veoTier, veoClipsPerScene, narrationRate, veoReady, veoFamilies, heroPhoto],
  );
  useEffect(() => {
    // 대본 단계에서만 그린다 — 작업 중·미리보기 중에 클립이 준비될 때마다 다시 그리면 생성·인코딩과 CPU 를 다툰다
    if (!script || step !== 'script') return undefined;
    let cancelled = false;
    const builderScenes = buildBuilderScenes(script);
    const timer = setTimeout(async () => {
      for (let i = 0; i < script.scenes.length; i++) {
        let dataUrl = '';
        try {
          dataUrl = await renderSceneThumbnail({
            scene: { ...builderScenes[i], videoBlobs: sceneClipBlobs(script.scenes[i], i, veoFamilies[0]) },
            productTitle: script.title,
            look: tone,
            route: script.route,
            googleMapsKey,
            imageCache: imageCacheRef.current,
          });
        } catch { /* 썸네일은 없어도 된다 */ }
        if (cancelled) return;
        setThumbs((prev) => ({ ...prev, [i]: dataUrl }));
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [thumbKey]);

  const updateScene = (idx, patch) => {
    setScript((prev) => {
      if (!prev) return prev;
      return { ...prev, scenes: prev.scenes.map((s, i) => (i === idx ? { ...s, ...patch } : s)) };
    });
  };

  // ── 나레이션 (TTS, 캐시) ──────────────────────────────────────
  const getNarration = useCallback(async (text, signal) => {
    const clean = String(text || '').trim();
    if (!clean) return null;
    const key = `${voice}|${tone}|${clean}`;
    const cached = ttsCacheRef.current.get(key);
    if (cached) return cached;
    const audio = await requestNarration({ text: clean, voice, tone, signal });
    const value = { samples: pcm16Base64ToFloat32(audio.data), sampleRate: audio.sampleRate };
    ttsCacheRef.current.set(key, value);
    refreshUsage();
    return value;
  }, [voice, tone, refreshUsage]);

  const listenNarration = useCallback(async (idx) => {
    const text = String(script?.scenes?.[idx]?.narration || '').trim();
    if (!text) return;
    stopPreviewAudio();
    setPreviewingIdx(idx);
    try {
      const a = await getNarration(text);
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const src = ctx.createBufferSource();
      src.buffer = floatToAudioBuffer(ctx, a.samples, a.sampleRate);
      src.connect(ctx.destination);
      src.onended = () => { if (previewSourceRef.current === src) { previewSourceRef.current = null; setPreviewingIdx(null); } };
      previewSourceRef.current = src;
      src.start();
    } catch (e) {
      setPreviewingIdx(null);
      setError(e?.message || String(e));
    }
  }, [script, getNarration, stopPreviewAudio]);

  // ── AI 영상 클립 (Veo, 고급) ──────────────────────────────────
  /**
   * 이 형식(family)에 필요한 클립을 전부 준비한다: 메모리 → IndexedDB → Veo 생성(동시 VEO_CONCURRENCY 개).
   * 돈이 드는 생성은 시작 전에 견적을 보여 주고 확인을 받는다. 실패한 클립은 기록만 하고 넘어간다(그 장면은 사진).
   */
  const ensureVeoClips = useCallback(async (family, signal) => {
    if (!canUseVeo || !veoPlan.length) return [];
    const jobs = [];
    for (const pl of veoPlan) for (const c of pl.clips) jobs.push({ idx: pl.idx, clip: c, key: veoKeyOf(c, family) });
    // 1) 캐시 확인 (메모리 → IndexedDB, 길이가 다른 변형도 인정 — veoReadyKey 참고)
    const todo = [];
    for (const j of jobs) {
      if (veoReadyKey(j.clip, family)) continue;
      let hit = null;
      for (const d of [j.clip.durationSeconds, ...VEO_SECS]) {
        const k = veoKeyOf(j.clip, family, d);
        hit = await getCachedClip(k);
        if (hit) { veoClipsRef.current.set(k, { blob: hit.blob, seconds: hit.seconds }); break; }
      }
      if (!hit) todo.push(j);
    }
    setVeoReady((n) => n + 1);
    if (!todo.length) return [];
    // 2) 생성 전 확인 창 — 여기서부터 유료. 클립마다 서버가 조립할 최종 프롬프트를 받아 보여 주고, 고친 글은 장면에 남긴다(promptOverrides)
    const tierLabel = VEO_TIERS.find((t) => t.id === veoTier)?.label || veoTier;
    const items = await Promise.all(todo.map(async (j) => {
      const k = veoPlan.find((p) => p.idx === j.idx)?.clips.indexOf(j.clip) ?? 0;
      let assembled = '';
      let assembledKo = '';
      try {
        const pv = await previewClipPrompt({ description: j.clip.description, descriptionKo: j.clip.descriptionKo, mode: j.clip.mode, hasImage: !!j.clip.imageUrl, hasReference: !!(j.clip.referenceUrl || j.clip.heroImageName), character: j.clip.character, signal });
        assembled = pv.prompt; assembledKo = pv.promptKo;
      } catch { /* 미리보기 실패해도 생성은 가능 */ }
      return { idx: j.idx, k, clip: j.clip, assembled, assembledKo, text: j.clip.prompt || assembled };
    }));
    const decided = await new Promise((resolve) => setVeoReview({ family, tierLabel, items, resolve }));
    setVeoReview(null);
    if (!decided) throw new DOMException('중단됨', 'AbortError');
    // 고친 프롬프트를 장면에 남기고(다음에도 같은 글로), 이번 작업의 키도 그 글로 바꾼다
    for (const it of decided) {
      const text = String(it.text || '').trim();
      const override = text && text !== it.assembled ? text : '';
      const j = todo.find((x) => x.idx === it.idx && x.clip === it.clip);
      if (!j) continue;
      if ((j.clip.prompt || '') !== override) {
        // 이번 작업은 원래 키로도 찾을 수 있게 둔다 — 이 프로젝트 조립은 확인 창 전의 계획(옛 키)으로 클립을 찾기 때문
        j.origKey = j.key;
        j.clip = { ...j.clip, prompt: override || undefined };
        j.key = veoKeyOf(j.clip, family);
        setScript((prev) => (prev ? { ...prev, scenes: prev.scenes.map((sc, i) => (i === it.idx ? { ...sc, promptOverrides: { ...(sc.promptOverrides || {}), [it.k]: override } } : sc)) } : prev));
      }
    }
    // 3) 생성
    const failed = [];
    let done = 0;
    setProgress({ stage: 'veo', done: 0, total: todo.length });
    const startedAt = Date.now();
    const tick = setInterval(() => setVeoElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    try {
      let next = 0;
      let lastStartAt = 0;
      const worker = async () => {
        for (;;) {
          const j = todo[next++];
          if (!j) return;
          if (signal?.aborted) throw new DOMException('중단됨', 'AbortError');
          // 시작 요청을 띄엄띄엄 — 400/429 처럼 즉시 실패하는 요청이 연달아 나가 한도를 태우지 않게
          const wait = lastStartAt + VEO_START_GAP_MS - Date.now();
          lastStartAt = Math.max(Date.now(), lastStartAt + VEO_START_GAP_MS);
          if (wait > 0) await new Promise((r) => setTimeout(r, wait));
          try {
            const image = j.clip.imageUrl ? await makeFirstFrameBase64(hiResOf(j.clip.imageUrl), imageCacheRef.current, family) : null;
            // 참고 사진은 자르지 않고 원본 비율 그대로(긴 변 1024px) — 구도가 아니라 장소·색감을 보라는 용도
            const reference = j.clip.referenceUrl ? await makeThumbnailBase64(hiResOf(j.clip.referenceUrl), imageCacheRef.current, 1024) : null;
            const heroImage = j.clip.heroImageName && heroPhoto?.url ? await makeThumbnailBase64(heroPhoto.url, imageCacheRef.current, 1024) : null;
            const r = await generateClip({ description: j.clip.description, prompt: j.clip.prompt, mode: j.clip.mode, image: image ? { data: image, mimeType: 'image/jpeg' } : undefined, reference: reference ? { data: reference, mimeType: 'image/jpeg' } : undefined, character: j.clip.character, heroImage: heroImage ? { data: heroImage, mimeType: 'image/jpeg' } : undefined, aspectRatio: family, durationSeconds: j.clip.durationSeconds, tier: veoTier, signal });
            veoClipsRef.current.set(j.key, { blob: r.blob, seconds: r.durationSeconds });
            if (j.origKey) veoClipsRef.current.set(j.origKey, { blob: r.blob, seconds: r.durationSeconds });
            putCachedClip(j.key, r.blob, { seconds: r.durationSeconds }).catch(() => {});
            refreshUsage();
            setVeoReady((n) => n + 1);
          } catch (e) {
            if (e?.name === 'AbortError') throw e;
            failed.push({ idx: j.idx, family, error: e?.message || String(e) });
          }
          done++;
          setProgress({ stage: 'veo', done, total: todo.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(VEO_CONCURRENCY, todo.length) }, worker));
    } finally {
      clearInterval(tick);
    }
    return failed;
  }, [canUseVeo, veoPlan, veoKeyOf, veoReadyKey, veoTier, hiResOf, refreshUsage, heroPhoto]);

  // 예능형 주인공 사진 (선택). Fast/Standard 의 "새 장면 생성" 클립에 참고 이미지로 같이 간다
  const onHeroPhoto = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setHeroPhoto((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { name: `${file.name}:${file.size}`, blob: file, url: URL.createObjectURL(file) }; });
  }, []);

  // 장면에 동영상 올리기 (mp4/webm). 실제 촬영 영상이 있으면 AI 클립보다 낫다 — 그 장면은 Veo 를 부르지 않는다.
  const onClipFile = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const idx = clipInputFor.current;
    if (idx == null || !files.length) return;
    setScript((prev) => {
      if (!prev) return prev;
      return { ...prev, scenes: prev.scenes.map((s, i) => {
        if (i !== idx) return s;
        const add = files.slice(0, 3).map((f) => ({ name: f.name, blob: f, url: URL.createObjectURL(f) }));
        return { ...s, userClips: [...(s.userClips || []), ...add].slice(0, 3) };
      }) };
    });
  }, []);

  // ── 2) 프로젝트 준비 (TTS + 사진) ─────────────────────────────
  /** 지금 대본·설정으로 형식 하나의 프로젝트를 만든다 (재료가 같으면 캐시된 것을 준다) */
  const ensureProject = useCallback(async (aspect, signal) => {
    // Blob 은 JSON 으로 구분이 안 되므로 사용자 동영상은 이름·크기로 서명한다
    const scenesSig = script.scenes.map((s) => ({ ...s, userClips: (s.userClips || []).map((c) => `${c.name}:${c.blob?.size}`) }));
    const key = JSON.stringify([{ ...script, scenes: scenesSig }, voice, tone, showSubtitles, veoMode, veoTier, veoClipsPerScene, narrationRate, musicMode, musicGain, musicFile?.name, musicDuck, sfx]);
    if (projectKeyRef.current !== key) { dropProjects(); projectKeyRef.current = key; }
    if (projectsRef.current[aspect]) return projectsRef.current[aspect];

    // AI 영상 클립부터 (유료·오래 걸림 — 견적 확인은 이 안에서). 실패한 장면은 사진으로 간다
    const family = veoFamilyOf(aspect);
    const failed = await ensureVeoClips(family, signal);
    // 형식(가로/세로)마다 따로 만드니 그 형식의 기록만 바꾼다
    setVeoFailed((prev) => [...prev.filter((f) => f.family !== family), ...failed]);

    // 장면마다 따로 음성을 만든다 — 장면 길이를 나레이션 길이에 정확히 맞추기 위해서다.
    let done = 0;
    setProgress({ stage: 'tts', done: 0, total: script.scenes.length });
    const audios = await Promise.all(script.scenes.map(async (s) => {
      const a = await getNarration(s.narration, signal);
      done++;
      setProgress({ stage: 'tts', done, total: script.scenes.length });
      return a;
    }));
    const scenes = buildBuilderScenes(script, (s, i) => ({
      imageUrls: s.imageUrls.map(hiResOf),
      audio: audios[i],
      videoBlobs: sceneClipBlobs(s, i, family),
    }));
    const project = await prepareProject({
      productTitle: script.title, scenes, aspect, look: tone, showSubtitles,
      route: script.route, googleMapsKey,
      narrationRate,
      music: { mode: musicMode === 'file' && !musicFile ? 'none' : musicMode, mood: tone, gain: musicGain, samples: musicFile?.samples, duck: musicDuck },
      sfx,
      imageCache: imageCacheRef.current, signal,
      onProgress: (p) => setProgress(p),
    });
    projectsRef.current[aspect] = project;
    return project;
  }, [script, voice, tone, showSubtitles, veoMode, veoTier, veoClipsPerScene, ensureVeoClips, sceneClipBlobs, getNarration, dropProjects, narrationRate, musicMode, musicGain, musicFile, musicDuck, sfx, hiResOf, googleMapsKey]);

  // 배경음악 파일 선택 → 48k mono 로 미리 디코딩해 둔다
  const onMusicFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const samples = await decodeAudioFile(file, 48000);
      setMusicFile({ name: file.name, samples });
      setMusicMode('file');
    } catch {
      setError('음악 파일을 읽지 못했습니다. mp3 / m4a / wav 파일을 선택해 주세요.');
    }
  }, []);

  const openPreview = useCallback(async (aspect) => {
    if (!script) return;
    setError('');
    stopPreviewAudio();
    playerRef.current?.destroy();
    playerRef.current = null;
    setStep('working');
    setVeoElapsed(0);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const project = await ensureProject(aspect, ac.signal);
      setPreviewAspect(aspect);
      setPreviewDuration(project.durationSec);
      setPreviewMissing(project.missingImages);
      setPreviewMissingClips(project.missingClips || 0);
      setPreviewTime(0);
      setPreviewPlaying(false);
      setStep('preview');
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
      setStep('script');
    } finally {
      setProgress(null);
    }
  }, [script, ensureProject, stopPreviewAudio]);

  // 미리보기 플레이어: preview 단계에 들어오면 캔버스에 붙이고, 나가면 정리
  useEffect(() => {
    if (step !== 'preview' || !canvasRef.current) return undefined;
    const project = projectsRef.current[previewAspect];
    if (!project) return undefined;
    const player = createPreview(project, canvasRef.current);
    let lastUi = 0;
    player.onTime((t, playing) => {
      const now = performance.now();
      if (playing && now - lastUi < 100) return; // 상태 갱신은 초당 10번이면 충분
      lastUi = now;
      setPreviewTime(t);
      setPreviewPlaying(playing);
    });
    playerRef.current = player;
    return () => { player.destroy(); if (playerRef.current === player) playerRef.current = null; };
  }, [step, previewAspect]);

  // ── 3) 내보내기 ────────────────────────────────────────────────
  const exportVideos = useCallback(async () => {
    if (!script) return;
    setError('');
    playerRef.current?.destroy();
    playerRef.current = null;
    setStep('working');
    const ac = new AbortController();
    abortRef.current = ac;
    const out = [];
    try {
      for (let i = 0; i < aspects.length; i++) {
        const aspect = aspects[i];
        const project = await ensureProject(aspect, ac.signal);
        const label = `${ASPECTS[aspect].label} (${i + 1}/${aspects.length})`;
        const r = await encodeProject(project, { signal: ac.signal, onProgress: (p) => setProgress({ ...p, label }) });
        out.push({ ...r, url: URL.createObjectURL(r.blob) });
      }
      setResults((prev) => { prev.forEach((r) => URL.revokeObjectURL(r.url)); return out; });
      setStep('done');
    } catch (e) {
      out.forEach((r) => URL.revokeObjectURL(r.url));
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
      setStep('preview');
    } finally {
      setProgress(null);
    }
  }, [script, aspects, ensureProject]);

  // ── 4) 첨부 ────────────────────────────────────────────────────
  const attachAsContent = useCallback(async () => {
    if (!results.length) return;
    setError('');
    setAttaching(true);
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      const title = attachTitle.trim() || 'AI 여행일정 소개 영상';
      // 콘텐츠 편집기의 "일반 작성(제목 없이 본문만)" 과 같은 방식: 컨텐츠명을 빈칸(' ') 으로 저장하면 상세페이지에 왼쪽 제목 칸 없이 본문만 나온다.
      const PLAIN_TITLE = ' ';
      const created = await createNewProductContent({ productNo: productId, companyNo, userNo, contentTypeCode: attachTypeCode || '', title: PLAIN_TITLE });
      const contentNo = Number(created?.table?.[0]?.컨텐츠번호 || created?.Table?.[0]?.컨텐츠번호 || created?.contentNo || 0);
      if (!contentNo) throw new Error('콘텐츠를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.');

      // 형식마다 파일을 올린다. 본문에는 가로(16:9)가 있으면 그것, 없으면 첫 파일을 재생기로 넣는다.
      const uploaded = [];
      for (const r of results) {
        const tag = r.aspect.replace(':', 'x');
        const file = new File([r.blob], `intro_${productId}_${tag}.mp4`, { type: 'video/mp4' });
        const up = await uploadContentFile({ file, productNo: productId, contentNo });
        if (!up || up.error) {
          throw new Error(`영상 파일(${ASPECTS[r.aspect].label}) 업로드에 실패했습니다. 파일이 크면 "다운로드" 로 받아 콘텐츠 편집의 파일 첨부로 직접 올려 주세요.`);
        }
        uploaded.push({ aspect: r.aspect, url: up.uploadResult?.url || up.uploadResult?.Url || `${S3_PUBLIC_PREFIX}${up.filePath}` });
      }
      const primary = uploaded.find((u) => u.aspect === '16:9') || uploaded[0];
      // 상세페이지 본문 재생기: 형식 비율의 상자를 먼저 잡아(aspect-ratio) 영상이 로드되기 전에도 자리가 흔들리지 않게 하고,
      // 자동재생(음소거·반복)으로 시작해 사용자가 controls 로 소리를 켠다. <source> 를 쓰는 건 기존 콘텐츠 편집기의 영상 마크업과 맞추기 위해서다.
      const ratio = primary.aspect.replace(':', ' / ');
      // 일반 작성이라 왼쪽 제목 칸이 없으니, 제목은 본문 맨 위 한 줄로 넣는다.
      const html = [
        `<div style="font-size: 18px; font-weight: 700; color: #222; margin: 0 0 10px; line-height: 1.4;">${esc(title)}</div>`,
        `<div style="position: relative; width: 100%; aspect-ratio: ${ratio}; overflow: hidden;">`,
        '<video autoplay muted loop playsinline preload="metadata" controls style="position: absolute; width: 100%; height: 100%; object-fit: contain; left: 0; top: 0;">',
        `<source src="${primary.url}" type="video/mp4">`,
        '브라우저에서 동영상을 재생할 수 없습니다.',
        '</video>',
        '</div>',
      ].join('');
      const saved = await saveProductContentInfo({
        productNo: productId, contentNo, companyNo, userNo, title: PLAIN_TITLE,
        text: `${title} (${Math.round(results[0].durationSec)}초, ${uploaded.map((u) => ASPECTS[u.aspect].label).join('/')})`,
        html, expose: true, dealerOnly: false, imageMappingUse: false, fileAttach: true,
      });
      if (saved?.error) throw new Error('콘텐츠 저장에 실패했습니다.');
      await onAttached?.({ contentNo, videoUrl: primary.url });
      alert('소개 영상이 콘텐츠로 첨부되었습니다.');
      onClose?.();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setAttaching(false);
    }
  }, [results, attachTitle, attachTypeCode, productId, onAttached, onClose]);

  const download = (r) => {
    const a = document.createElement('a');
    a.href = r.url;
    a.download = `${(productName || '상품').replace(/[\\/:*?"<>|]/g, '_')}_소개영상_${r.aspect.replace(':', 'x')}.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!open) return null;

  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const busy = step === 'working' || attaching;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-xs font-bold">AI</span>
            소개 영상 만들기
            <span className="text-xs font-normal text-gray-500">— 일정 데이터 + 상품 사진으로 짧은 영상</span>
          </h3>
          <div className="flex items-center gap-2">
            <StepChip n={1} label="대본" active={step === 'script'} done={step !== 'script'} />
            <StepChip n={2} label="미리보기" active={step === 'preview' || (step === 'working' && !results.length)} done={step === 'done'} />
            <StepChip n={3} label="첨부" active={step === 'done'} done={false} />
            <button type="button" className="ml-2 text-gray-400 hover:text-gray-700" onClick={() => onClose?.()} disabled={busy} title="닫기">✕</button>
          </div>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!isAiConfigured() && (
            <Notice tone="warn">AI 연결 설정이 없습니다. .env 에 REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY / REACT_APP_ERP_APP_TOKEN 를 넣고 개발 서버를 다시 시작하세요.</Notice>
          )}
          {!supported && (
            <Notice tone="warn">이 브라우저는 영상 조립(WebCodecs)을 지원하지 않습니다. 최신 Chrome 또는 Edge 에서 열어 주세요. 대본 작성까지는 가능합니다.</Notice>
          )}
          {error && <Notice tone="error">{error}</Notice>}

          {/* ── 1) 대본 ── */}
          {step === 'script' && (
            <>
                <div className="space-y-4">
                  {/* 설정 — 대본을 만들기 전에 정한다. 특히 길이는 대본 글자 수를 결정하므로 나중에 바꾸면 다시 만들어야 한다. */}
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-3 border rounded-lg p-3 bg-gray-50">
                    <Field label="형식">
                      <select className="w-full border rounded px-2 py-1.5 text-sm" value={scriptFormat} title={SCRIPT_FORMATS.find((f) => f.id === scriptFormat)?.hint} onChange={(e) => { const v = e.target.value; setScriptFormat(v); if (canUseVeo && v !== 'promo' && veoMode === 'off') setVeoMode('all'); }}>
                        {SCRIPT_FORMATS.map((f) => <option key={f.id} value={f.id} title={f.hint}>{f.label}</option>)}
                      </select>
                    </Field>
                    <Field label="톤">
                      <select className="w-full border rounded px-2 py-1.5 text-sm" value={tone} onChange={(e) => setTone(e.target.value)}>
                        {SCRIPT_TONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                    </Field>
                    <Field label="길이">
                      <select className="w-full border rounded px-2 py-1.5 text-sm" value={targetSec} onChange={(e) => setTargetSec(Number(e.target.value))}>
                        {DURATIONS.map((d) => <option key={d} value={d}>{d}초</option>)}
                      </select>
                    </Field>
                    <Field label="목소리">
                      <select className="w-full border rounded px-2 py-1.5 text-sm" value={voice} onChange={(e) => setVoice(e.target.value)}>
                        {NARRATION_VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                      </select>
                    </Field>
                    <Field label="출력 형식">
                      <div className="flex flex-col gap-0.5 text-xs pt-1">
                        {Object.entries(ASPECTS).map(([key, a]) => (
                          <label key={key} className="flex items-center gap-1 cursor-pointer" title={a.hint}>
                            <input type="checkbox" checked={aspects.includes(key)} onChange={(e) => setAspects((prev) => { const next = e.target.checked ? [...prev, key] : prev.filter((k) => k !== key); return next.length ? Object.keys(ASPECTS).filter((k) => next.includes(k)) : prev; })} />
                            {a.label}
                          </label>
                        ))}
                      </div>
                    </Field>
                    <Field label="옵션">
                      <div className="flex flex-col gap-0.5 text-xs pt-1">
                        <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={showSubtitles} onChange={(e) => setShowSubtitles(e.target.checked)} />나레이션 자막</label>
                        <label className="flex items-center gap-1 cursor-pointer" title="상품 사진 썸네일을 AI 에게 보여주고 장면마다 어울리는 사진을 고르게 합니다 (대본 다시 만들기 때 적용)"><input type="checkbox" checked={aiPickPhotos} onChange={(e) => setAiPickPhotos(e.target.checked)} />AI 가 사진 선택</label>
                      </div>
                    </Field>
                    <Field label="마무리 연락처">
                      <input className="w-full border rounded px-2 py-1.5 text-sm" value={contact} placeholder="회사명 · 전화" onChange={(e) => { const v = e.target.value; setContact(v); setScript((p) => (p && p.scenes.length > 1 ? { ...p, scenes: p.scenes.map((s, i) => (i === p.scenes.length - 1 ? { ...s, caption: v } : s)) } : p)); }} />
                    </Field>
                  </div>

                  {/* 예능형 주인공 — 대본을 만들기 전에 정한다. 외형(look)은 AI 가 대본과 함께 정하고 여기서 고칠 수 있다 */}
                  {scriptFormat === 'show' && (
                    <div className="border rounded-lg p-3 bg-amber-50/60 space-y-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="주인공 성별">
                          <select className="w-full border rounded px-2 py-1.5 text-sm" value={hero.gender} onChange={(e) => setHero((h) => ({ ...h, gender: e.target.value }))}>
                            {HERO_OPTIONS.gender.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                        </Field>
                        <Field label="연령대">
                          <select className="w-full border rounded px-2 py-1.5 text-sm" value={hero.age} onChange={(e) => setHero((h) => ({ ...h, age: e.target.value }))}>
                            {HERO_OPTIONS.age.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                        </Field>
                        <Field label="동행">
                          <select className="w-full border rounded px-2 py-1.5 text-sm" value={hero.companion} onChange={(e) => setHero((h) => ({ ...h, companion: e.target.value }))}>
                            {HERO_OPTIONS.companion.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                          </select>
                        </Field>
                        <Field label="주인공 사진 (선택)">
                          <div className="flex items-center gap-2 pt-0.5">
                            {heroPhoto ? <img src={heroPhoto.url} alt="" className="w-9 h-9 rounded-full object-cover border" /> : null}
                            <button type="button" className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50" onClick={() => heroInputRef.current?.click()}>{heroPhoto ? '바꾸기' : '올리기'}</button>
                            {heroPhoto && <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setHeroPhoto((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; })}>빼기</button>}
                            <input ref={heroInputRef} type="file" accept="image/*" className="hidden" onChange={onHeroPhoto} />
                          </div>
                        </Field>
                      </div>
                      {script?.hero ? (
                        <div>
                          <div className="text-[11px] font-medium text-gray-600 mb-1">주인공 외형 (영어, 모든 클립에 똑같이 붙습니다. 옷·머리·소지품을 바꾸려면 여기서 · 성별·연령대를 바꿨으면 "대본 다시 만들기")</div>
                          <textarea className="w-full border rounded px-2 py-1 text-[11px] leading-snug font-mono" rows={2} maxLength={400} value={script.hero.look} onChange={(e) => { const v = e.target.value; setScript((p) => (p ? { ...p, hero: { ...p.hero, look: v } } : p)); }} />
                        </div>
                      ) : (
                        <p className="text-[11px] text-gray-600">나레이션은 소개형과 같은 일정 소개이고, 영상만 이 주인공이 그 일정을 여행하는 모습을 3인칭으로 보여 줍니다. 대본을 만들면 AI 가 주인공 외형(옷·머리·소지품)을 정합니다. 모든 클립에 같은 외형이 붙어 같은 사람으로 보이게 하지만, 글만으로는 얼굴까지 완전히 같지는 않습니다 — Fast/Standard 에서 주인공 사진을 올리면 "새 장면 생성" 클립에 참고 이미지로 함께 보내 더 비슷해집니다(8초 고정). 사진의 초상권은 올리는 분이 확인해 주세요.</p>
                      )}
                    </div>
                  )}

                  {/* 소리 */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 border rounded-lg p-3 bg-gray-50">
                    <Field label="나레이션 속도">
                      <select className="w-full border rounded px-2 py-1.5 text-sm" value={narrationRate} onChange={(e) => setNarrationRate(Number(e.target.value))}>
                        {NARRATION_RATES.map((r) => <option key={r.value} value={r.value}>{r.label} ×{r.value}</option>)}
                      </select>
                    </Field>
                    <Field label="배경음악">
                      <select className="w-full border rounded px-2 py-1.5 text-sm" value={musicMode} onChange={(e) => { const v = e.target.value; if (v === 'file' && !musicFile) musicInputRef.current?.click(); else setMusicMode(v); }}>
                        {MUSIC_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                      <input ref={musicInputRef} type="file" accept="audio/*" className="hidden" onChange={onMusicFile} />
                    </Field>
                    <Field label={musicMode === 'file' ? (musicFile ? `파일: ${musicFile.name}` : '파일 없음') : '음악 음량'}>
                      <div className="flex items-center gap-2 pt-1">
                        <input type="range" min={0} max={1.5} step={0.05} value={musicGain} className="flex-1" disabled={musicMode === 'none'} onChange={(e) => setMusicGain(Number(e.target.value))} />
                        {musicMode === 'file' && <button type="button" className="text-[11px] text-violet-700 hover:underline shrink-0" onClick={() => musicInputRef.current?.click()}>바꾸기</button>}
                      </div>
                    </Field>
                    <Field label="소리 옵션">
                      <div className="flex flex-col gap-0.5 text-xs pt-1">
                        <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={sfx} onChange={(e) => setSfx(e.target.checked)} />장면 전환음</label>
                        <label className="flex items-center gap-1 cursor-pointer" title="켜면 나레이션이 나오는 동안 배경음악을 35% 로 낮췄다가 끝나면 다시 올립니다. 끄면 음악이 일정한 음량으로 깔립니다."><input type="checkbox" checked={musicDuck} disabled={musicMode === 'none'} onChange={(e) => setMusicDuck(e.target.checked)} />나레이션 중 음악 낮추기</label>
                      </div>
                    </Field>
                    <div className="text-[11px] text-gray-500 self-end pb-1">
                      자동 생성 음악은 브라우저가 합성하는 저작권 없는 깔림음입니다.
                    </div>
                  </div>

                  {/* 대본 근거 — 일정표 + 상세정보 글. 무엇이 AI 에게 가는지 보여 주고, 빼고 싶은 글은 체크를 푼다. */}
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-xs font-semibold text-gray-700">대본 근거 데이터</div>
                      <div className="text-[11px] text-gray-500">일정표 {Array.isArray(itineraryData?.table1) ? new Set(itineraryData.table1.map((r) => Number(r?.일차)).filter((n) => n > 0)).size : 0}일 · 방문순서 {useVisitOrder ? visitOrder.length : 0}곳 · 상세정보 글 {extrasAll.length - extrasOff.size}/{extrasAll.length}개</div>
                    </div>
                    {/* 주요 관광지 방문순서 — 일정표 편집 영역의 그 데이터. 경로 지도의 점 순서가 된다 */}
                    <div className="mb-1.5 text-xs">
                      {visitOrder.length ? (
                        <label className="flex items-start gap-1 cursor-pointer" title="일정표 편집의 '주요 관광지 방문순서'. 대본의 방문지 힌트로 쓰이고, 이 순서대로 경로 지도의 점을 찍습니다.">
                          <input type="checkbox" className="mt-0.5" checked={useVisitOrder} onChange={(e) => setUseVisitOrder(e.target.checked)} />
                          <span className={useVisitOrder ? '' : 'text-gray-400 line-through'}>
                            <span className="font-medium">주요 관광지 방문순서</span>
                            <span className="text-gray-500"> ({visitOrder.length}곳) </span>
                            {visitOrder.join(' → ')}
                          </span>
                        </label>
                      ) : (
                        <p className="text-[11px] text-amber-700">주요 관광지 방문순서가 비어 있습니다. 일정표 편집 영역에서 등록하면 대본 힌트와 경로 지도의 점 순서로 씁니다. (없으면 AI 가 일차별 대표 장소로 경로를 잡습니다)</p>
                      )}
                    </div>
                    {extrasAll.length ? (
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {extrasAll.map((e, i) => (
                          <label key={`${e.title}-${i}`} className="flex items-center gap-1 text-xs cursor-pointer" title={e.text.slice(0, 300)}>
                            <input type="checkbox" checked={!extrasOff.has(i)} onChange={(ev) => setExtrasOff((prev) => { const next = new Set(prev); if (ev.target.checked) next.delete(i); else next.add(i); return next; })} />
                            <span className={extrasOff.has(i) ? 'text-gray-400 line-through' : ''}>{e.title}</span>
                            <span className="text-gray-400">{e.text.length}자</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-500">상세정보 콘텐츠(투어소개·판매정보·포함사항 등)에 글이 없어 일정표만으로 대본을 씁니다. 상세정보에 글을 등록하면 대본 근거로 함께 보냅니다.</p>
                    )}
                    {script && extrasAll.length > 0 && scriptFormat !== 'story' && (
                      <p className="text-[11px] mt-1.5">
                        {script.keyPoints?.length
                          ? <><span className="text-gray-600">AI 가 상세정보에서 뽑은 포인트: </span><span className="text-violet-700">{script.keyPoints.join(' · ')}</span></>
                          : <span className="text-amber-700">AI 가 상세정보에서 포인트를 뽑지 못했습니다. 글이 사무적인 내용(요금·규정)뿐이면 그럴 수 있습니다. 대본을 다시 만들어 보세요.</span>}
                      </p>
                    )}
                    {script && (
                      <p className="text-[11px] mt-1">
                        {script.route?.length >= 2
                          ? <><span className="text-gray-600">경로 지도: </span><span className="text-violet-700">{script.route.map((r) => r.name).join(' → ')}</span><span className="text-gray-400"> · 경로 장면 + 일차 전환 미니맵</span></>
                          : <span className="text-gray-500">경로 지도 없음 — 일정표의 "주요 관광지 방문순서"를 채우면 지도 위에 경로가 그려집니다.</span>}
                      </p>
                    )}
                    {script && <p className="text-[11px] text-gray-500 mt-1">체크를 바꾼 뒤에는 "대본 다시 만들기"를 눌러야 반영됩니다.</p>}
                  </div>

                  {scriptLoading && (
                    <div className="py-10 text-center text-sm text-gray-600">
                      <Spinner />
                      {progress?.stage === 'photos' ? `사진 ${progress.done}/${progress.total}장 준비 중…` : `일정과 사진을 보고 ${targetSec}초 대본을 쓰는 중입니다…`}
                    </div>
                  )}
                  {!scriptLoading && !script && (
                    <div className="py-10 text-center border-2 border-dashed rounded-lg">
                      <p className="text-sm text-gray-600 mb-3">위에서 형식·톤·길이·목소리를 정한 뒤 대본을 만드세요. 길이({targetSec}초)에 맞춰 나레이션 {Math.round(targetSec * 5)}~{targetSec * 6}자로 씁니다.{scriptFormat === 'story' ? (canUseVeo ? ' 체험형은 1인칭 순간들로 쓰고, AI 영상(Veo)이 "모든 장면"으로 켜집니다.' : ' 체험형은 1인칭 순간들로 씁니다.') : scriptFormat === 'show' ? (canUseVeo ? ' 예능형은 나레이션은 일정 소개 그대로, 영상만 위 주인공이 여행하는 모습을 3인칭으로 만듭니다(AI 영상 "모든 장면").' : ' 예능형은 나레이션은 일정 소개 그대로, 주인공이 여행하는 느낌의 대본으로 씁니다.') : ''}</p>
                      <button type="button" className="px-5 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={generateScript} disabled={!isAiConfigured()}>{error ? '다시 시도' : '대본 만들기'}</button>
                    </div>
                  )}
                </div>
              {script && !scriptLoading && (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="영상 제목 (인트로 화면)">
                      <input className="w-full border rounded px-2 py-1.5 text-sm" value={script.title} onChange={(e) => { const v = e.target.value; setScript((p) => ({ ...p, title: v, scenes: p.scenes.map((s, i) => (i === 0 ? { ...s, title: v } : s)) })); }} />
                    </Field>
                    <Field label="부제 (인트로 아래 한 줄)">
                      <input className="w-full border rounded px-2 py-1.5 text-sm" value={script.subtitle} onChange={(e) => { const v = e.target.value; setScript((p) => ({ ...p, subtitle: v, scenes: p.scenes.map((s, i) => (i === 0 ? { ...s, caption: v } : s)) })); }} />
                    </Field>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      장면 {script.scenes.length}개 · 나레이션 {totalNarrationChars}자
                      <span className={`ml-1 ${totalNarrationChars < targetSec * 4.5 ? 'text-amber-600' : 'text-gray-400'}`}>
                        (목표 {Math.round(targetSec * 5)}~{targetSec * 6}자 ≈ {targetSec}초. {totalNarrationChars < targetSec * 4.5 ? '목표보다 짧아 영상이 짧아집니다 — 문장을 늘리거나 다시 만드세요.' : '영상 길이는 나레이션에 맞춰 자동으로 정해집니다.'})</span>
                    </div>
                    <button type="button" className="text-xs text-violet-700 hover:underline disabled:opacity-50" onClick={generateScript} disabled={scriptLoading}>
                      {scriptLoading ? '다시 쓰는 중…' : '대본 다시 만들기 (형식·톤·길이 적용)'}
                    </button>
                  </div>

                  <ol className="space-y-3">
                    {script.scenes.map((s, i) => (
                      <li key={i} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex gap-3">
                          <div className="w-56 shrink-0">
                            <div className="aspect-video w-full rounded-md overflow-hidden bg-gray-200 border">
                              {thumbs[i] ? <img src={thumbs[i]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[11px] text-gray-400">미리보기 준비 중</div>}
                            </div>
                            {(s.userClips || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1 items-center">
                                {s.userClips.map((c, k) => (
                                  <button key={`${c.name}-${k}`} type="button" title={`${c.name} — 빼기`} className="relative w-12 h-9 rounded overflow-hidden border group bg-black" onClick={() => { URL.revokeObjectURL(c.url); updateScene(i, { userClips: s.userClips.filter((x) => x !== c) }); }}>
                                    <video src={c.url} muted playsInline className="w-full h-full object-cover" />
                                    <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/50 text-white text-xs">✕</span>
                                  </button>
                                ))}
                                <span className="text-[11px] text-emerald-700">내 동영상 사용 (사진·AI 대신)</span>
                              </div>
                            )}
                            {(
                              <div className="mt-2 flex flex-wrap gap-1 items-center">
                                {s.imageUrls.map((u) => (
                                  <button key={u} type="button" title="이 사진 빼기" className="relative w-12 h-9 rounded overflow-hidden border group" onClick={() => updateScene(i, { imageUrls: s.imageUrls.filter((x) => x !== u) })}>
                                    <img src={u} alt="" className="w-full h-full object-cover" />
                                    <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/50 text-white text-xs">✕</span>
                                  </button>
                                ))}
                                {s.imageUrls.length < 3 && candidates.length > 0 && (
                                  <div className="relative">
                                    <button type="button" className="w-12 h-9 rounded border border-dashed text-gray-500 text-lg leading-none hover:bg-white" title="사진 추가" onClick={() => setImagePickerFor(imagePickerFor === i ? null : i)}>＋</button>
                                    {imagePickerFor === i && (
                                      <div className="absolute z-20 left-0 top-10 bg-white border rounded-lg shadow-lg p-2 w-72 max-h-56 overflow-y-auto">
                                        <div className="grid grid-cols-4 gap-1">
                                          {candidates.filter((c) => !s.imageUrls.includes(c.url)).map((c) => (
                                            <button key={c.url} type="button" title={c.source} className="aspect-video rounded overflow-hidden border hover:ring-2 ring-violet-500" onClick={() => { updateScene(i, { imageUrls: [...s.imageUrls, c.url].slice(0, 3) }); setImagePickerFor(null); }}>
                                              <img src={c.url} alt="" className="w-full h-full object-cover" />
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {(s.userClips || []).length < 3 && (
                                  <button type="button" className="w-12 h-9 rounded border border-dashed text-gray-500 text-[10px] leading-tight hover:bg-white" title="이 장면에 내 동영상(mp4/webm) 올리기 — 사진과 AI 클립 대신 씁니다" onClick={() => { clipInputFor.current = i; clipInputRef.current?.click(); }}>동영상<br />올리기</button>
                                )}
                                {!s.imageUrls.length && !(s.userClips || []).length && <span className="text-[11px] text-amber-600 ml-1">사진 없음 → 단색 배경</span>}
                              </div>
                            )}
                            {canUseVeo && veoMode !== 'off' && !(s.userClips || []).length && (() => {
                              const kind = sceneKind(i, script.scenes.length, s);
                              const on = sceneVeoEnabled(s, kind, veoMode);
                              const plan = veoPlan.find((p) => p.idx === i);
                              const ready = plan ? plan.clips.filter((c) => veoReadyKey(c, veoFamilies[0])).length : 0;
                              const noPhoto = !s.imageUrls.filter(Boolean).length;
                              const shotMode = shotModeOf(s);
                              return (
                                <div className="mt-2 space-y-1">
                                  <label className="flex items-center gap-1 text-[11px] cursor-pointer">
                                    <input type="checkbox" checked={on} disabled={kind === 'route'} onChange={(e) => updateScene(i, { veo: e.target.checked })} />
                                    <span className={on ? 'text-violet-700 font-medium' : 'text-gray-500'}>AI 영상{kind === 'route' ? ' (경로 장면은 지도)' : ''}</span>
                                    {plan && <span className="text-gray-400">· {plan.clips.map((c) => `${c.durationSeconds}초`).join('+')}{plan.clips[0]?.referenceUrl ? ' · 사진 참고' : ''}{ready ? ` · 준비됨 ${ready}/${plan.clips.length}` : ''}</span>}
                                  </label>
                                  {on && (
                                    <>
                                      <select className="w-full border rounded px-1.5 py-1 text-[11px]" value={shotMode} title={SHOT_MODES.find((m) => m.id === shotMode)?.hint} onChange={(e) => updateScene(i, { shotMode: e.target.value })}>
                                        {SHOT_MODES.map((m) => <option key={m.id} value={m.id} disabled={noPhoto && m.id !== 'story'}>{m.label}{noPhoto && m.id !== 'story' ? ' (사진 필요)' : ''}</option>)}
                                      </select>
                                      <textarea className="w-full border rounded px-1.5 py-1 text-[11px] leading-snug" rows={3} value={s.shot || ''} maxLength={300} placeholder={shotMode === 'photo' ? `카메라·분위기 (영어 권장). 비우면: ${defaultShot(i)}` : '무엇이 보이고 누가 무엇을 하는지 (영어 권장). 예: A Korean traveler tosses a coin over her shoulder into the fountain'} title={SHOT_MODES.find((m) => m.id === shotMode)?.hint} onChange={(e) => updateScene(i, { shot: e.target.value, promptOverrides: undefined })} />
                                      {s.shotKo && (
                                        <p className="text-[11px] text-gray-500 leading-snug" title="대본을 만들 때 AI 가 함께 적은 한국어 뜻입니다. 영상에는 위 영어가 쓰이고, 영어를 고치면 이 뜻은 갱신되지 않습니다.">뜻: {s.shotKo}</p>
                                      )}
                                      {s.promptOverrides && Object.values(s.promptOverrides).some(Boolean) && (
                                        <p className="text-[11px] text-violet-700">확인 창에서 고친 최종 프롬프트를 씁니다. <button type="button" className="underline" onClick={() => updateScene(i, { promptOverrides: undefined })}>서버 조립으로 되돌리기</button></p>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-semibold text-gray-500 w-6">#{i + 1}</span>
                              <input className="w-24 border rounded px-2 py-1 text-xs" value={s.label} placeholder="라벨(1일차)" onChange={(e) => updateScene(i, { label: e.target.value })} />
                              <input className="flex-1 border rounded px-2 py-1 text-sm font-medium" value={s.title} placeholder="화면 제목" onChange={(e) => updateScene(i, { title: e.target.value })} />
                            </div>
                            <input className="w-full border rounded px-2 py-1 text-xs" value={s.caption} placeholder="화면 캡션 (한 줄)" onChange={(e) => updateScene(i, { caption: e.target.value })} />
                            <div className="flex gap-2 items-start">
                              <textarea className="flex-1 border rounded px-2 py-1 text-sm leading-relaxed" rows={2} value={s.narration} placeholder="나레이션 (실제로 읽히고, 자막으로도 표시되는 문장)" onChange={(e) => updateScene(i, { narration: e.target.value })} />
                              <button type="button" className="shrink-0 px-2 py-1 text-xs rounded border bg-white hover:bg-gray-100 disabled:opacity-50" onClick={() => (previewingIdx === i ? stopPreviewAudio() : listenNarration(i))} disabled={!s.narration?.trim() || (previewingIdx !== null && previewingIdx !== i)} title="이 문장을 선택한 목소리로 들어봅니다 (같은 문장은 다시 만들지 않습니다)">
                                {previewingIdx === i ? '■ 정지' : '▶ 듣기'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>

                  {/* 고급: AI 영상(Veo) — 업체번호 1번 전용. 장면 사진을 첫 프레임으로 넣어 움직이는 클립을 만든다 */}
                  {canUseVeo && (
                    <details className="border rounded-lg p-3 bg-gray-50" open={veoMode !== 'off'}>
                      <summary className="text-xs font-medium text-gray-600 cursor-pointer">고급 — 장면 사진을 AI 영상(Veo)으로 움직이기 (유료, 초당 과금)</summary>
                      <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Field label="적용 범위">
                            <select className="w-full border rounded px-2 py-1.5 text-sm" value={veoMode} onChange={(e) => setVeoMode(e.target.value)}>
                              {VEO_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                            </select>
                          </Field>
                          <Field label="품질 등급">
                            <select className="w-full border rounded px-2 py-1.5 text-sm" value={veoTier} disabled={veoMode === 'off'} onChange={(e) => setVeoTier(e.target.value)}>
                              {VEO_TIERS.map((t) => <option key={t.id} value={t.id}>{t.label} · ${t.usdPerSec.toFixed(2)}/초</option>)}
                            </select>
                          </Field>
                          <Field label="장면당 컷 수">
                            <select className="w-full border rounded px-2 py-1.5 text-sm" value={veoClipsPerScene} disabled={veoMode === 'off'} onChange={(e) => setVeoClipsPerScene(Number(e.target.value))}>
                              <option value={1}>1컷 (사진 한 장을 길게)</option>
                              <option value={2}>2컷 (사진 두 장, 빠른 편집감)</option>
                            </select>
                          </Field>
                          <div className="text-[11px] self-end pb-1">
                            {veoMode === 'off' ? (
                              <span className="text-gray-500">꺼짐 — 사진 켄번즈로 만듭니다.</span>
                            ) : (
                              <span className="text-violet-800">
                                클립 {veoEstimate.clips}개 · {veoEstimate.seconds}초 · 형식 {veoFamilies.length}종 → <b>약 ${veoEstimate.usd.toFixed(2)}</b>
                                {veoEstimate.cachedSeconds > 0 && <span className="text-gray-500"> (이미 만든 것 빼면 ${veoEstimate.usdRemaining.toFixed(2)})</span>}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          장면마다 나레이션이 말하는 장면을 4~8초 클립으로 만들고, 자막·나레이션·음악은 그대로 얹습니다. 방식은 AI 가 장면별로 고르고 카드에서 바꿀 수 있습니다 —
                          <b>사진 움직이기</b>(사진 그대로), <b>사진 속 연출</b>(그 장소에서 사람의 행동), <b>새 장면 생성</b>(기차 창밖·인솔자·전용차량처럼 사진에 없는 장면 — Fast/Standard 는 장면 사진을 참고해 비슷한 장소·분위기로, 8초 고정. 실제와 다를 수 있음).
                          등장인물은 한국인 여행자·인솔자로, 글자·로고·얼굴 클로즈업 금지는 서버가 항상 붙입니다. 세로(9:16) 형식은 세로 클립을 따로 만들어 비용이 2배가 됩니다.
                          생성 직전에 견적을 다시 확인받고, 성공한 클립만 과금되며, 같은 재료의 클립은 이 브라우저에 14일간 보관돼 다시 만들지 않습니다.
                          Google 한도는 등급(모델)별 분당 4건·하루 50건입니다 — 하루에 만들 수 있는 클립 수가 정해져 있으니, 넘으면 다른 등급으로 바꾸거나 다음 날 이어서 만드세요.
                          안전필터 등으로 실패한 장면은 사진으로 대신합니다. 장면마다 체크로 켜고 끌 수 있고, 실제 촬영 동영상이 있으면 "동영상 올리기"가 가장 좋습니다.
                        </p>
                      </div>
                    </details>
                  )}
                  <input ref={clipInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" multiple className="hidden" onChange={onClipFile} />
                </div>
              )}
            </>
          )}

          {/* ── 생성 전 확인 창: 최종 프롬프트를 클립마다 보여 주고 고칠 수 있다. 여기서 "생성 시작" 을 눌러야 유료 호출이 나간다 ── */}
          {veoReview && (
            <VeoReviewDialog review={veoReview} tier={veoTier} onChange={(items) => setVeoReview((r) => (r ? { ...r, items } : r))} onCancel={() => veoReview.resolve(null)} onConfirm={() => veoReview.resolve(veoReview.items)} />
          )}

          {/* ── 작업 중 ── */}
          {step === 'working' && !veoReview && (
            <div className="py-16 max-w-md mx-auto text-center">
              <div className="text-sm font-medium text-gray-800 mb-1">{STAGE_LABEL[progress?.stage] || '준비 중'}…</div>
              {progress?.label && <div className="text-xs text-gray-500 mb-3">{progress.label}</div>}
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full bg-violet-600 transition-all ${progress?.stage === 'veo' ? 'animate-pulse' : ''}`} style={{ width: progress?.stage === 'veo' ? `${Math.max(8, pct)}%` : `${pct}%` }} />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {progress?.stage === 'veo' ? `${progress.done} / ${progress.total} 완료 · ${veoElapsed}초 경과 — Google 서버에서 영상을 그리는 중입니다` : progress ? `${progress.done} / ${progress.total}` : ''}
              </div>
              <p className="mt-6 text-xs text-gray-500">이 창을 닫지 마세요. 영상은 이 브라우저 안에서 만들어집니다.</p>
              <button type="button" className="mt-4 px-3 py-1.5 text-xs rounded border hover:bg-gray-50" onClick={() => abortRef.current?.abort()}>중단</button>
            </div>
          )}

          {/* ── 2) 미리보기 ── */}
          {step === 'preview' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-1">
                  {aspects.map((a) => (
                    <button key={a} type="button" className={`px-2.5 py-1 text-xs rounded-full border ${previewAspect === a ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-gray-50'}`} onClick={() => { if (a !== previewAspect) openPreview(a); }}>
                      {ASPECTS[a].label}
                    </button>
                  ))}
                  <span className="ml-2 text-[11px] text-gray-500">{ASPECTS[previewAspect].hint}</span>
                </div>
                <div className="text-xs text-gray-500">
                  {Math.round(previewDuration)}초 · 인코딩 전 실시간 미리보기
                  {previewMissing > 0 && <span className="ml-2 text-amber-600">불러오지 못한 사진 {previewMissing}장은 건너뜁니다.</span>}
                  {previewMissingClips > 0 && <span className="ml-2 text-amber-600">재생할 수 없는 동영상 {previewMissingClips}개는 사진으로 대신했습니다 (mp4/H.264 로 변환해 올려 주세요).</span>}
                </div>
              </div>
              {veoFailed.length > 0 && (
                <Notice tone="warn">AI 영상 클립 {veoFailed.length}개는 만들지 못해 그 장면은 사진으로 대신했습니다: {veoFailed.map((f) => `#${f.idx + 1} (${f.error})`).join(' · ')} — 대본 수정에서 지시문을 바꿔 다시 시도할 수 있습니다.</Notice>
              )}
              <div className="flex justify-center bg-gray-900 rounded-lg p-2">
                <canvas ref={canvasRef} className="rounded cursor-pointer" style={{ maxHeight: '58vh', maxWidth: '100%', height: 'auto', width: 'auto', aspectRatio: `${ASPECTS[previewAspect].width} / ${ASPECTS[previewAspect].height}` }} onClick={() => (previewPlaying ? playerRef.current?.pause() : playerRef.current?.play())} />
              </div>
              <div className="flex items-center gap-3">
                <button type="button" className="w-9 h-9 rounded-full bg-violet-600 text-white hover:bg-violet-700 flex items-center justify-center" onClick={() => (previewPlaying ? playerRef.current?.pause() : playerRef.current?.play())} title={previewPlaying ? '일시정지' : '재생'}>
                  {previewPlaying ? '❚❚' : '▶'}
                </button>
                <input type="range" min={0} max={previewDuration || 0} step={0.05} value={previewTime} className="flex-1" onChange={(e) => { const t = Number(e.target.value); setPreviewTime(t); playerRef.current?.seek(t); }} />
                <span className="text-xs text-gray-600 tabular-nums w-20 text-right">{fmtTime(previewTime)} / {fmtTime(previewDuration)}</span>
              </div>
              <p className="text-[11px] text-gray-500">자막·사진·문장이 마음에 안 들면 "대본 수정" 으로 돌아가세요. 나레이션은 문장이 바뀐 장면만 다시 만듭니다.</p>
            </div>
          )}

          {/* ── 4) 결과·첨부 ── */}
          {step === 'done' && results.length > 0 && (
            <div className="space-y-4">
              <div className={`grid gap-3 ${results.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                {results.map((r) => (
                  <div key={r.aspect} className="border rounded-lg p-2 bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{ASPECTS[r.aspect].label} · {r.width}×{r.height}</span>
                      <span className="text-[11px] text-gray-500">{Math.round(r.durationSec)}초 · {(r.blob.size / 1024 / 1024).toFixed(1)}MB · H.264 {(r.videoBitrate / 1e6).toFixed(1)}Mbps/{r.audioCodec === 'aac' ? 'AAC' : 'Opus'}</span>
                    </div>
                    <video src={r.url} controls playsInline className="w-full rounded bg-black" style={{ maxHeight: '48vh' }} />
                    <div className="mt-1 text-right">
                      <button type="button" className="text-xs text-violet-700 hover:underline" onClick={() => download(r)} disabled={attaching}>다운로드</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border rounded-lg p-3 bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="콘텐츠 종류">
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={attachTypeCode} onChange={(e) => setAttachTypeCode(e.target.value)}>
                    {contentTypes.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
                    {!contentTypes.length && <option value="">(종류 없음)</option>}
                  </select>
                </Field>
                <Field label="콘텐츠 제목 (본문 맨 위에 들어갑니다)">
                  <input className="w-full border rounded px-2 py-1.5 text-sm" value={attachTitle} onChange={(e) => setAttachTitle(e.target.value)} />
                </Field>
                <p className="md:col-span-2 text-[11px] text-gray-500">"일반 작성(제목 없이 본문만)" 형태로 첨부됩니다 — 상세페이지에 왼쪽 제목 칸 없이 제목 한 줄과 영상이 본문으로 나옵니다. 첨부하면 이 상품에 새 콘텐츠가 만들어지고, 영상 파일{results.length > 1 ? '들' : ''}이 첨부파일로 올라간 뒤 본문에 {results.length > 1 ? '가로 영상' : '영상'} 재생기가 들어갑니다. 세로·정사각 파일은 첨부파일 목록에서 받아 SNS 에 올리면 됩니다.</p>
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
          <div className="text-[11px] text-gray-500">
            {step === 'script' && (veoMode === 'off' ? 'AI 는 대본·목소리·사진 선택만 합니다. 화면에는 이 상품에 등록된 실제 사진만 쓰입니다.' : `AI 영상 켜짐 — 미리보기 때 클립을 만들기 전에 견적(약 $${veoEstimate.usdRemaining.toFixed(2)})을 다시 확인합니다.`)}
            {step === 'preview' && `내보내기: ${aspects.map((a) => ASPECTS[a].label).join(', ')}`}
            {step === 'done' && '마음에 안 들면 "미리보기" 나 "대본 수정" 으로 돌아가 다시 만들 수 있습니다.'}
            {(usage.script.calls > 0 || usage.tts.calls > 0 || usage.veo.calls > 0) && (
              <span className="block mt-0.5 text-gray-400" title="응답에 실려 온 usageMetadata 합계. 출력에는 모델의 생각(thinking) 토큰이 포함됩니다. 비용은 공개 단가로 계산한 추정치이고, 같은 문장의 나레이션은 캐시돼 다시 청구되지 않습니다.">
                이 영상 AI 사용량 — 대본 {usage.script.calls}회 (입력 {usage.script.input.toLocaleString()} · 출력 {usage.script.output.toLocaleString()} 토큰)
                {usage.tts.calls > 0 && ` · 나레이션 ${usage.tts.calls}회 (음성 ${usage.tts.output.toLocaleString()} 토큰)`}
                {usage.veo.calls > 0 && ` · AI 영상 ${usage.veo.calls}클립 ${usage.veo.seconds}초 ≈ $${usage.veo.usd.toFixed(2)}`}
                {usage.usd != null ? ` ≈ 합계 $${usage.usd.toFixed(3)}` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 'script' && (
              <>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => onClose?.()}>닫기</button>
                <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={() => openPreview(aspects[0])} disabled={!script || scriptLoading || !supported || !isAiConfigured()}>
                  미리보기 만들기 →
                </button>
              </>
            )}
            {step === 'preview' && (
              <>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => { playerRef.current?.pause(); setStep('script'); }}>← 대본 수정</button>
                <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700" onClick={exportVideos}>
                  mp4 만들기 ({aspects.length}개 형식) →
                </button>
              </>
            )}
            {step === 'done' && (
              <>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => setStep('preview')} disabled={attaching}>← 미리보기</button>
                <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={() => setStep('script')} disabled={attaching}>대본 수정</button>
                <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50" onClick={attachAsContent} disabled={attaching}>
                  {attaching ? '첨부하는 중…' : '콘텐츠로 첨부'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 생성 전 확인 창. 만들 클립마다 방식·길이·첫 사진과 서버가 조립한 최종 프롬프트를 보여 주고 그 자리에서 고칠 수 있다.
 * 고친 글은 서버 조립 대신 그대로 Veo 에 간다(장면에 남아 다음에도 쓰인다). 총 비용을 다시 보여 주고 "생성 시작" 을 눌러야 과금이 시작된다.
 */
function VeoReviewDialog({ review, tier, onChange, onCancel, onConfirm }) {
  const { items, family, tierLabel } = review;
  const seconds = items.reduce((n, it) => n + it.clip.durationSeconds, 0);
  const usd = seconds * veoUsdPerSec(tier);
  const modeLabel = (m) => SHOT_MODES.find((x) => x.id === m)?.label || m;
  const setText = (i, text) => onChange(items.map((it, j) => (j === i ? { ...it, text } : it)));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-gray-800">생성 전 확인 — 클립 {items.length}개 · {seconds}초 · {tierLabel} · {family}</div>
          <div className="text-[11px] text-gray-500">아래 글이 Veo 에 그대로 갑니다. 고치면 그 글로 만들고 장면에 남겨 둡니다. 예상 비용 <b className="text-violet-800">약 ${usd.toFixed(2)}</b> (성공한 클립만 과금, 클립당 1~3분).</div>
        </div>
      </div>
      <ol className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
        {items.map((it, i) => (
          <li key={`${it.idx}-${it.k}`} className="border rounded-lg p-2 bg-gray-50 flex gap-2">
            <div className="w-28 shrink-0">
              {it.clip.imageUrl || it.clip.referenceUrl
                ? <img src={it.clip.imageUrl || it.clip.referenceUrl} alt="" className="w-28 h-16 object-cover rounded border" />
                : <div className="w-28 h-16 rounded border border-dashed flex items-center justify-center text-[10px] text-gray-400">사진 없음</div>}
              <div className="mt-1 text-[10px] text-gray-500 leading-tight">#{it.idx + 1}{it.k ? ` · ${it.k + 1}컷` : ''} · {modeLabel(it.clip.mode)} · {it.clip.durationSeconds}초{it.clip.referenceUrl ? ' · 사진 참고' : ''}{it.clip.heroImageName ? ' · 주인공 사진' : ''}{it.clip.imageUrl ? ' · 첫 프레임' : ''}</div>
            </div>
            <div className="flex-1 min-w-0">
              <textarea className="w-full border rounded px-2 py-1 text-[11px] leading-snug font-mono" rows={6} maxLength={1500} value={it.text} onChange={(e) => setText(i, e.target.value)} />
              {it.assembledKo && (
                <details className="mt-1">
                  <summary className="text-[10px] text-gray-500 cursor-pointer">한국어로 읽기 (서버 조립 기준 — 영어를 고치면 이 번역은 바뀌지 않습니다)</summary>
                  <pre className="mt-1 text-[11px] leading-snug whitespace-pre-wrap text-gray-700 bg-white border rounded px-2 py-1">{it.assembledKo}</pre>
                </details>
              )}
              {it.text.trim() !== it.assembled.trim() && (
                <div className="text-[10px] text-violet-700 mt-0.5">고친 글로 만듭니다 · <button type="button" className="underline" onClick={() => setText(i, it.assembled)}>서버 조립으로 되돌리기</button></div>
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="flex items-center justify-end gap-2">
        <button type="button" className="px-3 py-1.5 text-sm rounded border hover:bg-white" onClick={onCancel}>취소 (대본으로)</button>
        <button type="button" className="px-4 py-1.5 text-sm rounded bg-violet-600 text-white hover:bg-violet-700" onClick={onConfirm}>이 프롬프트로 생성 시작 (약 ${usd.toFixed(2)})</button>
      </div>
    </div>
  );
}

export function StepChip({ n, label, active, done }) {
  const cls = active ? 'bg-violet-600 text-white' : done ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}><span>{done ? '✓' : n}</span>{label}</span>;
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export function Notice({ tone, children }) {
  const cls = tone === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-800';
  return <div className={`mb-3 border rounded-lg px-3 py-2 text-xs ${cls}`}>{children}</div>;
}

export function Spinner() {
  return <span className="inline-block w-4 h-4 mr-2 align-middle border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />;
}
